/* sts-liquid-quiz.js — Liquid Quiz PoC engine (liquid-proof-of-concept)
 *
 * Vanilla ES2019, classic script (defer), no build step, no imports.
 * Spec: skin-type-pro/docs/superpowers/specs/2026-08-06-liquid-quiz-poc.md
 *
 * Owns: view state machine (landing/quiz/calculating/results), questions
 * fetch + rendering (all 4 types), conditional visibility, validation,
 * email gate (+honeypot, +optional Turnstile), submit with the widget's
 * exact retry budgets and byte-identical body, outcome handling, the
 * localStorage allow-list write, per-type CSS custom properties, and the
 * window.SLQ.track telemetry helper.
 *
 * window.SLQ contract (shared with sts-liquid-quiz-results.js):
 *   SLQ.track(event, detail)       — telemetry (auto-includes widgetToken)
 *   SLQ.state                      — {widgetToken, skinType, sensitivityLevel, ...} after success
 *   SLQ.settings                   — {nextBase, ec2Base, proxyBase, useProxySubmit, turnstileSiteKey}
 *   SLQ.go(view)                   — switch view shells
 *   SLQ.brighten(hex)              — prototype :871 hex lighten (per-type theming)
 *   SLQ.typeInfo(code)             — entry from window.__STS_SKIN_TYPE_INFO__ or null
 *   SLQ.results.show(state)        — provided by the RESULTS file; if absent when
 *                                    success lands, SLQ.pendingResult is set and the
 *                                    results file must consume it on load.
 *
 * No secrets, no Lambda/API-Gateway URLs, no PII in telemetry or URLs.
 */
(function () {
  'use strict';

  /* -- Every request on this page has a deadline -------------------------------
     A fetch that hangs neither resolves nor rejects, so a `.catch()` alone is
     not a safety net: it only runs when the request FAILS, and a dead socket
     does not count as a failure. That is how the "calculating" screen ran its animation forever after a submit that never
     came back - no outcome panel, no error, no retry, and the answers still in
     place - and a hung question load left the loading panel up with the quiz
     hidden behind it.
     A deadline turns a hang into the ordinary rejection each caller below was
     already written to handle.
     12000ms is a "the network is gone" stop, not a performance budget --
     aborting a slow-but-alive request would be worse than waiting for it. */
  var NET_TIMEOUT_MS = 12000;

  function netFetch(url, opts, ms) {
    var limit = ms || NET_TIMEOUT_MS;
    var o = {};
    var k;
    for (k in (opts || {})) { if (Object.prototype.hasOwnProperty.call(opts, k)) o[k] = opts[k]; }
    /* A caller that brought its own AbortController keeps it -- overwriting the
       signal would silently disarm an abort somebody else relies on. That call
       is capped by the race below instead. */
    var ctrl = (!o.signal && typeof window.AbortController === 'function')
      ? new window.AbortController() : null;
    if (ctrl) o.signal = ctrl.signal;
    var timer = window.setTimeout(function () {
      if (ctrl) { try { ctrl.abort(); } catch (e) { /* already gone */ } }
    }, limit);
    var req = fetch(url, o);
    var race = ctrl ? req : Promise.race([req, new Promise(function (_, reject) {
      window.setTimeout(function () { reject(new Error('timeout')); }, limit);
    })]);
    return race.then(
      function (r) { window.clearTimeout(timer); return r; },
      function (e) {
        window.clearTimeout(timer);
        /* An abort otherwise reads as "Failed to fetch", which some callers put
           in front of the shopper. Say what actually happened. */
        throw (e && e.name === 'AbortError') ? new Error('timeout') : e;
      }
    );
  }

  var SLQ = (window.SLQ = window.SLQ || {});

  /* One place decides which backend the theme talks to: snippets/sts-env.liquid
     resolves the sts.api_env / sts.*_base_* shop metafields and publishes
     window.STS.env. The section setting (already rendered into the data
     attribute) stays as the fallback; going through env is also what applies
     the "dev never runs on the published theme" refusal. The ?slqEnv= override
     below is a per-visit thing that wins over both, off the published theme only. */
  function stsEnvBase(key, fallback, hard) {
    var E = window.STS && window.STS.env;
    return String((E && E.get) ? E.get(key, fallback || hard) : (fallback || hard));
  }

  var WIDGET_VERSION = 'liquid-poc-1';
  var PAGE_CONTEXT = 'quiz-page-liquid';
  var ASSIGNMENTS = { concept: 'liquid-poc' };
  // + routine_picker: the picker A/B arm (assets/sts-ab.js), read per send.
  function assignments() { var A = window.STS && window.STS.ab; return A ? A.assignments(ASSIGNMENTS) : ASSIGNMENTS; }

  // Retry budgets — copied from the headless widget (spec §1.3/§5)
  var NETWORK_MAX_RETRIES = 2;
  var NETWORK_RETRY_DELAY_MS = 1000;
  var IN_FLIGHT_MAX_ATTEMPTS = 24;
  /* The submit waits for the scoring Lambda (15-25 s on a slow day; the proxy
     allows 65 s). The generic 12 s network deadline aborted it mid-run and
     retried, and a retry is exactly what raced a staged result into a plain
     "recovered" success (2026-10-13). A real hang still ends here. */
  var SUBMIT_TIMEOUT_MS = 45000;
  /* A "recovered" answer is the server rebuilding a result the first request
     never recorded. For a returning email that should be STAGED, re-ask a few
     times: once the first request finishes, the ledger replays its exact
     answer (the staging envelope). */
  var RECOVERED_RECHECK_MAX = 4;
  var RECOVERED_RECHECK_DELAY_MS = 3000;
  var DETERMINISTIC_NO_RETRY_STATUSES = [400, 401, 403];
  var CALC_MIN_DISPLAY_MS = 2500;
  var CALC_STAGE_MS = 950; // prototype startCalc() cadence

  // The display mode option buttons are built with (renderQuestion ~:728).
  // Anything that re-shows a hidden option MUST restore this, never '' — see
  // applyAffiliateOptionRules.
  var OPT_DISPLAY = 'flex';

  // SEC-01: an affiliate code is 2-20 letters and digits, with single inner
  // hyphens for practice location codes (SMITH-L5), or it is not used.
  // Keep in step with AFF_RE in skin-type-pro app/api/quiz/validate-affiliate/route.ts.
  var AFF_RE = /^(?=.{2,20}$)[A-Z0-9]+(?:-[A-Z0-9]+)*$/i;

  // Canonical spelling (snippets/sts-skin-types.liquid, spec 05 §8).
  var AXES = { D: 'Dry', O: 'Oily', S: 'Sensitive', R: 'Resistant', P: 'Pigmented', N: 'Non-pigmented', W: 'Wrinkle-prone', T: 'Tight' };
  // The v4 neutral set (spec 08 §3.2) — :root in assets/sts-tokens.css.
  var NEUTRAL_RAMP = { hex: '#7B85B4', deep: '#2F3572', tint: '#EEF0F8' };

  // ---------------------------------------------------------------- utilities

  var root = null;

  function $(sel, scope) { return (scope || root).querySelector(sel); }
  function $all(sel, scope) { return Array.prototype.slice.call((scope || root).querySelectorAll(sel)); }

  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    // RFC4122 v4 via getRandomValues (older Safari)
    var bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    var hex = [];
    for (var i = 0; i < 16; i++) hex.push((bytes[i] + 0x100).toString(16).slice(1));
    return hex.slice(0, 4).join('') + '-' + hex.slice(4, 6).join('') + '-' + hex.slice(6, 8).join('') + '-' + hex.slice(8, 10).join('') + '-' + hex.slice(10, 16).join('');
  }

  function ssGet(key) { try { return window.sessionStorage.getItem(key); } catch (e) { return null; } }
  function ssSet(key, val) { try { window.sessionStorage.setItem(key, val); } catch (e) { /* private mode */ } }
  function lsGet(key) { try { return window.localStorage.getItem(key); } catch (e) { return null; } }
  function lsSet(key, val) { try { window.localStorage.setItem(key, val); } catch (e) { /* private mode */ } }

  function delay(ms) { return new Promise(function (resolve) { window.setTimeout(resolve, ms); }); }

  function stripTrailingSlash(s) { return String(s || '').replace(/\/+$/, ''); }

  // ------------------------------------------------------------------ session

  function sessionUuid() {
    var existing = ssGet('slq_session_uuid');
    if (existing) return existing;
    var fresh = uuid();
    ssSet('slq_session_uuid', fresh);
    return fresh;
  }

  function nextClientSeq() {
    var raw = parseInt(ssGet('slq_client_seq') || '0', 10);
    if (!isFinite(raw) || raw < 0) raw = 0;
    var next = raw + 1;
    ssSet('slq_client_seq', String(next));
    return next;
  }

  // Analytics notes: docs/new-theme-v4/asset-notes/sts-liquid-quiz.md.
  // No cookie-banner check (decision 2026-09-28: same as live, where a No
  // changes nothing). The P2 consent gate that stood here was removed.
  function gaEvent(key, params) {
    try {
      var ga = SLQ.settings && SLQ.settings.ga;
      var name = ga && ga[key];
      if (!name) return;
      var payload = { event_category: ga.category || 'Custom Event' };
      if (params && typeof params === 'object') {
        Object.keys(params).forEach(function (k) {
          if (params[k] !== undefined && params[k] !== null && params[k] !== '') payload[k] = params[k];
        });
      }
      gaSend(name, payload);
      if (key === 'landed' || key === 'started' || key === 'completed') {
        metaSend(name, payload);
      }
      if (window.Northbeam && typeof window.Northbeam.fireCustomGoal === 'function') {
        window.Northbeam.fireCustomGoal(name, {});
      }
    } catch (e) { /* analytics must never break the quiz */ }
  }
  SLQ.gaEvent = gaEvent;

  var metaQueue = [];
  var metaWaiter = null;
  var metaWaitedMs = 0;
  function metaReady() {
    return typeof window.fbq === 'function' && typeof window.fbq.getState === 'function';
  }
  function metaSend(name, payload) {
    var eid = 'slq-' + uuid();
    if (metaReady()) {
      window.fbq('trackCustom', name, payload, { eventID: eid });
      return;
    }
    metaQueue.push({ name: name, payload: payload, eid: eid });
    if (metaWaiter) return;
    metaWaiter = window.setInterval(function () {
      metaWaitedMs += 250;
      var ready = metaReady();
      if (!ready && metaWaitedMs < 20000) return;
      window.clearInterval(metaWaiter);
      metaWaiter = null;
      var q = metaQueue.splice(0);
      if (!ready) return; // no pixel on this page (blocked) — drop quietly
      q.forEach(function (item) { window.fbq('trackCustom', item.name, item.payload, { eventID: item.eid }); });
    }, 250);
  }

  var gaQueue = [];
  var gaWaiter = null;
  var gaWaitedMs = 0;
  function gaSend(name, payload) {
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, payload);
      return;
    }
    gaQueue.push({ name: name, payload: payload });
    if (gaWaiter) return;
    gaWaiter = window.setInterval(function () {
      gaWaitedMs += 250;
      var ready = typeof window.gtag === 'function';
      if (!ready && gaWaitedMs < 20000) return;
      window.clearInterval(gaWaiter);
      gaWaiter = null;
      var q = gaQueue.splice(0);
      q.forEach(function (item) {
        if (ready) {
          window.gtag('event', item.name, item.payload);
        } else if (window.dataLayer && typeof window.dataLayer.push === 'function') {
          var dl = { event: item.name };
          Object.keys(item.payload).forEach(function (k) { dl[k] = item.payload[k]; });
          window.dataLayer.push(dl);
        }
      });
    }, 250);
  }

  function gaQuestionFields(q) {
    return {
      question_id: q && q.id,
      question_title: q && q.text ? String(q.text).slice(0, 100) : undefined,
      question_type: q && q.type
    };
  }

  // ---------------------------------------------------------------- telemetry
  // Spec §7: envelope {token?, events:[...]}, POST {ec2_base}/widget/events.
  // Required per event: event_uuid, session_uuid, client_seq (monotonic int,
  // persisted in sessionStorage), occurred_at (ISO), event. No PII ever.

  SLQ.track = function track(eventName, detail) {
    try {
      if (!SLQ.settings || !SLQ.settings.ec2Base) return;
      var evt = {
        event_uuid: uuid(),
        session_uuid: sessionUuid(),
        client_seq: nextClientSeq(),
        occurred_at: new Date().toISOString(),
        event: String(eventName),
        widget_version: WIDGET_VERSION,
        page_context: PAGE_CONTEXT,
        assignments: assignments(),
        device_class: window.innerWidth <= 760 ? 'mobile' : 'desktop'
      };
      if (typeof state.configVersionInt === 'number') evt.config_version = state.configVersionInt;
      if (detail && typeof detail === 'object') {
        // question_code/step_index are TOP-LEVEL event columns (migration 212;
        // the weekly evidence pack groups on question_code) — hoist them out
        // of detail so the server actually stores them.
        if (typeof detail.question_code === 'string') { evt.question_code = detail.question_code; }
        if (typeof detail.step_index === 'number') { evt.step_index = detail.step_index; }
        evt.detail = detail;
      }
      var envelope = { events: [evt] };
      if (SLQ.state && SLQ.state.widgetToken) envelope.token = SLQ.state.widgetToken;
      var url = stripTrailingSlash(SLQ.settings.ec2Base) + '/widget/events';
      var payload = JSON.stringify(envelope);

      // sendBeacon FIRST: add_to_cart fires immediately before the cart
      // navigation, and fetch+keepalive only *sometimes* survives that unload
      // (observed: cart-add events intermittently missing server-side). Beacon
      // is queued by the browser and delivered regardless of navigation. The
      // events endpoint accepts text/plain exactly for this path.
      try {
        if (navigator.sendBeacon) {
          var blob = new Blob([payload], { type: 'text/plain;charset=UTF-8' });
          if (navigator.sendBeacon(url, blob)) return;
        }
      } catch (e) { /* fall through to fetch */ }

      /* NOT netFetch: this is a keepalive beacon, and a keepalive request is
         meant to outlive the page. An abort timer would kill exactly the ones
         sent on unload, which are the ones worth having. Fire-and-forget, with
         a catch, and it paints nothing. */
      window.fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: payload,
        keepalive: true
      }).catch(function () { /* telemetry is fire-and-forget */ });
    } catch (e) { /* never break the quiz for telemetry */ }
  };

  // ---------------------------------------------- per-type theming (spec §3)

  // Port of prototype brighten() (:871) — hex -> HSL lighten to l=.72
  function brighten(hex) {
    var r = parseInt(hex.slice(1, 3), 16) / 255;
    var g = parseInt(hex.slice(3, 5), 16) / 255;
    var b = parseInt(hex.slice(5, 7), 16) / 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b);
    var h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h /= 6;
    }
    if (l >= 0.68) return hex;
    return hslToHex(h, s, 0.72);
  }

  function hslToHex(h, s, l) {
    var hue2rgb = function (p, q, t) {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    var p2 = 2 * l - q;
    var to = function (x) { return Math.round(hue2rgb(p2, q, x) * 255).toString(16).padStart(2, '0'); };
    return '#' + to(h + 1 / 3) + to(h) + to(h - 1 / 3);
  }

  function hexToHsl(hex) {
    var r = parseInt(hex.slice(1, 3), 16) / 255;
    var g = parseInt(hex.slice(3, 5), 16) / 255;
    var b = parseInt(hex.slice(5, 7), 16) / 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b);
    var h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h /= 6;
    }
    return { h: h, s: s, l: l };
  }

  SLQ.brighten = brighten;

  SLQ.typeInfo = function typeInfo(code) {
    if (!code) return null;
    var info = window.__STS_SKIN_TYPE_INFO__;
    if (!info || typeof info !== 'object') return null;
    return info[code] || info[String(code).toUpperCase()] || null;
  };

  function isHex(v) { return typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v); }

  // One ramp (spec 08 DS-02, §3.7): snippets/sts-head.liquid prints the 16-type
  // table, colours included, as #sts-skin-types — the same values
  // assets/sts-tokens.css paints v4 pages with and the results page reads.
  // Null when the element is missing or unparseable; callers then fall back.
  var sharedRampCache;
  function sharedRamp(code) {
    if (sharedRampCache === undefined) {
      sharedRampCache = null;
      try {
        var el = document.getElementById('sts-skin-types');
        var data = el ? JSON.parse(el.textContent || 'null') : null;
        if (data && typeof data === 'object') sharedRampCache = data;
      } catch (e) { /* malformed JSON: fall back */ }
    }
    var entry = sharedRampCache && sharedRampCache[String(code || '').toUpperCase()];
    return entry && isHex(entry.hex) && isHex(entry.deep) && isHex(entry.tint) ? entry : null;
  }

  // Sets the slq- custom properties on BOTH documentElement (contract for the
  // results file) and the section root (wins over any stylesheet default).
  // `code` is a 4-letter type, or null for the neutral set. A type takes the
  // shared ramp; without the JSON it falls back to the old behaviour — deep/tint
  // derived from the shop metafield's colour — and does nothing if that is
  // missing too.
  function setTypeColors(code) {
    var pal = code ? sharedRamp(code) : NEUTRAL_RAMP;
    if (!pal) {
      var info = SLQ.typeInfo(code);
      var metaHex = info && info.color;
      if (!isHex(metaHex)) return;
      var hsl = hexToHsl(metaHex);
      pal = {
        hex: metaHex,
        deep: hslToHex(hsl.h, hsl.s, Math.max(0.18, hsl.l - 0.14)),
        tint: hslToHex(hsl.h, Math.min(1, hsl.s), Math.min(0.94, Math.max(hsl.l, 0.88)))
      };
    }
    var hex = pal.hex;
    var deep = pal.deep;
    var tint = pal.tint;
    var bright = brighten(hex);
    // Octagon fill under a white glyph (--sts-type-oct, one palette site-wide);
    // the metafield fallback has none, and its deep is the nearest dark-enough.
    var oct = isHex(pal.oct) ? pal.oct : code ? deep : hex;
    [document.documentElement, root].forEach(function (el) {
      if (!el) return;
      el.style.setProperty('--slq-type-oct', oct);
      // both spellings: short names (inline styles in the section/snippet
      // markup + results engine) and long-form names (sts-liquid-quiz.css)
      el.style.setProperty('--slq-type', hex);
      el.style.setProperty('--slq-type-color', hex);
      el.style.setProperty('--slq-type-bright', bright);
      el.style.setProperty('--slq-type-color-bright', bright);
      el.style.setProperty('--slq-type-deep', deep);
      el.style.setProperty('--slq-type-color-deep', deep);
      el.style.setProperty('--slq-type-tint', tint);
      el.style.setProperty('--slq-type-color-tint', tint);
    });
  }

  // ------------------------------------- conditional visibility (verbatim port
  // of lib/quiz-core/index.ts:5-102)

  function evaluateRule(rule, answers) {
    var answer = answers[rule.target_question_code];
    if (!answer) return false;
    var answerValue = Array.isArray(answer) ? answer : [answer];
    switch (rule.operator) {
      case 'equals':
        return answerValue.indexOf(rule.value_code) !== -1;
      case 'not_equals':
        return answerValue.indexOf(rule.value_code) === -1;
      case 'contains':
        return answerValue.some(function (val) { return String(val).indexOf(rule.value_code) !== -1; });
      case 'not_contains':
        return !answerValue.some(function (val) { return String(val).indexOf(rule.value_code) !== -1; });
      case 'includes':
        return answerValue.indexOf(rule.value_code) !== -1;
      case 'excludes':
        return answerValue.indexOf(rule.value_code) === -1;
      default:
        return false;
    }
  }

  function evaluateQuestionConditions(question, answers) {
    if (!question.conditions || question.conditions.length === 0) return true;
    for (var i = 0; i < question.conditions.length; i++) {
      var condition = question.conditions[i];
      var results = condition.rules.map(function (rule) { return evaluateRule(rule, answers); });
      var conditionMet = condition.type === 'OR'
        ? results.some(function (r) { return r; })
        : results.every(function (r) { return r; });
      if (!conditionMet) return false;
    }
    return true;
  }

  function getVisibleQuestions(allQuestions, answers) {
    return allQuestions.filter(function (q) { return evaluateQuestionConditions(q, answers); });
  }

  /* The counter's total ("Question 12 of 18"). The visible count grows the
     moment a conditional question's trigger is answered (QA 2026-10-01: "12 of
     17" became "13 of 18"), so the total counts every question that can still
     appear: a conditional one drops out only once an answer already given rules
     it out, and the total can only go down as the quiz moves forward. Display
     only: what is asked, in what order, and what is scored and sent still come
     from evaluateQuestionConditions above. Never below the visible count. */
  function possibleQuestionCount(allQuestions, answers) {
    var byId = {};
    var memo = {};
    var has = Object.prototype.hasOwnProperty;
    allQuestions.forEach(function (q) { byId[q.id] = q; });
    function ruleCanHold(rule) {
      var t = rule.target_question_code;
      if (has.call(answers, t)) return evaluateRule(rule, answers);
      // unanswered: it can still hold only if its question can still be asked
      return has.call(byId, t) && canShow(byId[t]);
    }
    function canShow(q) {
      if (has.call(memo, q.id)) return memo[q.id];
      memo[q.id] = true; // a condition loop counts as possible
      var ruledOut = (q.conditions || []).some(function (c) {
        var held = (c.rules || []).map(ruleCanHold);
        return c.type === 'OR' ? !held.some(Boolean) : !held.every(Boolean);
      });
      memo[q.id] = !ruledOut;
      return memo[q.id];
    }
    return allQuestions.filter(canShow).length;
  }

  function getNextVisibleQuestion(allQuestions, currentIndex, answers, direction) {
    var increment = direction === 'previous' ? -1 : 1;
    var nextIndex = currentIndex + increment;
    while (nextIndex >= 0 && nextIndex < allQuestions.length) {
      if (evaluateQuestionConditions(allQuestions[nextIndex], answers) &&
          !accountAnswered(allQuestions[nextIndex], answers)) return nextIndex;
      nextIndex += increment;
    }
    return -1;
  }

  // ------------------------------------------- validation ports (payload.ts +
  // widget Question.tsx isValidMonthYear)

  // lib/quiz-core/payload.ts:23-40 — returns null when valid, else message
  function validateName(raw) {
    var trimmed = String(raw || '').trim();
    if (!trimmed) return 'Please enter your full name.';
    if (trimmed.length > 80) return 'Name is too long.';
    if (/[@/]|https?:|www\.|\d/.test(trimmed)) {
      return 'Please enter a valid name (letters only).';
    }
    var parts = trimmed.split(/\s+/).filter(Boolean);
    if (parts.length < 2) return 'Please enter both your first and last name.';
    var NAME_PART_RE = /^[A-Za-zÀ-ÖØ-öø-ÿ][A-Za-zÀ-ÖØ-öø-ÿ'\-.]{1,}$/;
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i];
      if (p.length < 2) return 'Each part of your name must be at least 2 letters.';
      if (!NAME_PART_RE.test(p)) return 'Please enter a valid name (letters, hyphens, apostrophes only).';
    }
    return null;
  }

  // lib/quiz-core/payload.ts:48-66
  function validateAnswer(question, answer) {
    if (!question) return false;
    if (question.id === 'q17') {
      var value = Array.isArray(answer) ? (answer[0] || '') : (answer || '');
      return validateName(value) === null;
    }
    if (question.required) {
      if (Array.isArray(answer)) return answer.length > 0;
      return typeof answer === 'string' && answer.trim().length > 0;
    }
    return true;
  }

  // Index (into state.questions) of the first CURRENTLY VISIBLE question that
  // has no valid answer, or -1 when the set is complete.
  //
  // Forward play cannot leave a hole — every Continue runs validateAnswer — so
  // the only way to reach the gate with a partial set is a restored checkpoint
  // whose currentIndex outruns its answers. sessionStorage is editable, and a
  // questionnaire edit can shift the list under a saved index (the `version`
  // guard is inert while the API reports the constant "default"). A partial
  // set scores as a 500, which the shopper cannot recover from — the retry
  // button re-sends the same partial set — so both the resume path and submit
  // check this. Visibility is evaluated against the CURRENT answers so a
  // conditional question that does not apply (e.g. pregnancy for a male
  // respondent) can never block a legitimate submission.
  function firstIncompleteQuestionIndex() {
    var visible = getVisibleQuestions(state.questions, state.answers);
    for (var i = 0; i < visible.length; i++) {
      if (!validateAnswer(visible[i], state.answers[visible[i].id])) {
        return state.questions.indexOf(visible[i]);
      }
    }
    return -1;
  }

  // Where a resume lands: the first visible question with no answer yet, which
  // is the {next} the Q~ strip and the home hero promise. Unlike the check
  // above, an optional question counts only once it has been through Continue:
  // a skip stores '' (or []) and is answered, a never-seen one has no key.
  function isAnswered(q, answers) {
    return !!q && Object.prototype.hasOwnProperty.call(answers, q.id) && validateAnswer(q, answers[q.id]);
  }

  function firstUnansweredQuestionIndex() {
    var visible = getVisibleQuestions(state.questions, state.answers);
    for (var i = 0; i < visible.length; i++) {
      if (!isAnswered(visible[i], state.answers)) return state.questions.indexOf(visible[i]);
    }
    return -1;
  }

  // q17 name, q18 birth month/year, q19 ZIP. sts_quiz_progress never holds
  // them (sts-state-quiz-bridge.js strips them), so the home strip counts them
  // as still to do. A resume drops them here too, from memory and from the tab
  // copy, so it lands on the first of them and the visitor re-enters all three
  // instead of resuming past them on answers the rest of the site cannot see.
  var IDENTITY_QUESTION_IDS = ['q17', 'q18', 'q19'];

  function resumeAtFirstUnanswered() {
    if (!Object.keys(state.answers || {}).length) return false;
    var hadIdentity = false;
    for (var k = 0; k < IDENTITY_QUESTION_IDS.length; k++) {
      if (Object.prototype.hasOwnProperty.call(state.answers, IDENTITY_QUESTION_IDS[k])) {
        delete state.answers[IDENTITY_QUESTION_IDS[k]];
        hadIdentity = true;
      }
    }
    if (!Object.keys(state.answers).length) return false;
    var at = firstUnansweredQuestionIndex();
    if (at !== -1) state.currentIndex = at;
    if (hadIdentity) saveProgress();
    return true;
  }

  /* Turn whatever the visitor has typed into MM/YYYY as they go, so they never
     have to type the "/" themselves — a numeric keyboard has no slash key,
     which made this question unanswerable on a phone.

       "2"    -> "02/"     a first digit above 1 can only be a single-digit
                           month, so pad it and move straight to the year
       "1"    -> "1"       still ambiguous (1, 10, 11, 12) — wait for the next
       "04"   -> "04/"     month complete, slash inserted
       "041985" -> "04/1985"

     `deleting` suppresses the auto-slash during a backspace: re-appending it
     would restore the character the visitor is trying to remove and trap the
     caret at the slash forever. */
  function formatMonthYear(raw, deleting) {
    var d = String(raw == null ? '' : raw).replace(/\D/g, '').slice(0, 6);
    if (d.length === 1 && d > '1') d = '0' + d;
    if (d.length <= 2) return (d.length === 2 && !deleting) ? d + '/' : d;
    return d.slice(0, 2) + '/' + d.slice(2);
  }

  // widget Question.tsx:151-158 — MM/YYYY, month 01-12, year 1920..current
  function isValidMonthYear(value) {
    var match = /^(\d{2})\/(\d{4})$/.exec(value);
    if (!match) return false;
    var month = parseInt(match[1], 10);
    var year = parseInt(match[2], 10);
    var currentYear = new Date().getFullYear();
    return month >= 1 && month <= 12 && year >= 1920 && year <= currentYear;
  }

  // ------------------------------------------ signed-in identity prefill
  /* q17 name, q18 MM/YYYY and q16 background arrive filled in for a signed-in
     customer and stay editable; Continue saves what is on screen through the
     normal path (same validation, same question_answered / GA events).
     Regression guard (2026-07 birthday kick-out: an observer rewrote the
     FOCUSED input on every DOM change): this never touches an input.
     renderQuestion seeds state.draft before building the field, only for an
     unanswered question, and state.prefilled makes it once per field per page,
     so nothing the customer typed or changed is written again. No observer,
     no focus/blur, no events. Guests get nothing: the data is server-rendered
     for a signed-in customer only ([data-slq-prefill]), never localStorage. */
  var PREFILL_IDS = { q16: 1, q17: 1, q18: 1 };
  var prefillSrc;

  function prefillSource() {
    // SIMULATOR ONLY: STS.preview exists only past sts-simulator.js's gates
    // (never the published theme); a preview row reads its fixture, nothing real.
    var P = window.STS && window.STS.preview;
    if (P && P.active) return (P.fixture && P.fixture.identity) || null;
    if (prefillSrc !== undefined) return prefillSrc;
    prefillSrc = null;
    var el = document.querySelector('[data-slq-prefill]');
    if (!el) return null;
    var a = null;
    try {
      a = JSON.parse(el.getAttribute('data-quiz') || 'null');
      if (typeof a === 'string') a = JSON.parse(a);
    } catch (e) { a = null; }
    if (a && a.answers && typeof a.answers === 'object') a = a.answers;
    if (!a || typeof a !== 'object') a = {};
    /* lastQuizResults keys ("<code>::<category>::<id>") changed over the
       years: match the code. q16/q17/q18 are the widget's; first_name,
       BirthMonth ("04::April", Octane "04/1985"), BirthYear, ethnicity::…
       the Lambda's. */
    var pick = function (re) {
      for (var k in a) {
        if (Object.prototype.hasOwnProperty.call(a, k) && re.test(k) && a[k] != null && typeof a[k] !== 'object') {
          var v = String(a[k]).trim();
          if (v) return v;
        }
      }
      return '';
    };
    var name = ((el.getAttribute('data-first') || '') + ' ' + (el.getAttribute('data-last') || '')).trim() ||
      pick(/^q17::/) || (pick(/^first_name$/) + ' ' + pick(/^last_name$/)).trim() || pick(/^first_name:/);
    var birth = pick(/^q18::/);
    if (!isValidMonthYear(birth)) {
      var m = /^(\d{1,2})(?:\/(\d{4}))?/.exec(pick(/^birthmonth::/i));
      birth = m ? ('0' + m[1]).slice(-2) + '/' + (m[2] || pick(/^birthyear::/i)) : '';
    }
    prefillSrc = {
      name: name.replace(/\s+/g, ' '),
      birth: isValidMonthYear(birth) ? birth : '',
      ethnicity: pick(/^q16::/) || pick(/^ethnicity::/i)
    };
    return prefillSrc;
  }

  /* Signed-in retake (Dr. Baumann, 2026-10-13): a signed-in customer is not
     asked for what the account already knows. Name (q17) and birth month/year
     (q18) are answered from the account up front and never shown; the email
     is the account's, so the email pop-up is skipped too (finishQuestions).
     Only a value that passes the question's own validation is used: a missing
     or odd name/birth date still shows its question, prefilled as before.
     The answers go to the server exactly as if typed, so scoring is the same.
     state.accountAnswered marks them; navigation and the counter skip them,
     while omitHiddenAnswers (conditions only) still sends them. */
  var ACCOUNT_ANSWER_IDS = ['q17', 'q18'];

  function accountAnswered(q, answers) {
    return !!(q && state.accountAnswered && state.accountAnswered[q.id] &&
      validateAnswer(q, (answers || state.answers)[q.id]));
  }

  function seedAccountAnswers() {
    if (!gateLoggedInEmail() || !state.questions || !state.questions.length) return;
    var src = prefillSource();
    if (!src) return;
    ACCOUNT_ANSWER_IDS.forEach(function (id) {
      if (Object.prototype.hasOwnProperty.call(state.answers, id)) return;
      var q = null;
      for (var i = 0; i < state.questions.length; i++) if (state.questions[i].id === id) q = state.questions[i];
      var v = id === 'q17' ? src.name : src.birth;
      if (!q || !v || !validateAnswer(q, v)) return;
      state.answers[id] = v;
      state.accountAnswered[id] = true;
      state.prefilled[id] = true;
    });
  }

  // `word` sits in `hay` as a whole answer: option values carry commas
  // ("Asian-East (China, Japan, …)"), so the saved list cannot be split.
  function hasToken(hay, word) {
    for (var i = word ? hay.indexOf(word) : -1; i !== -1; i = hay.indexOf(word, i + 1)) {
      var after = hay.charAt(i + word.length);
      if ((!i || /[\s,#]/.test(hay.charAt(i - 1))) && (!after || /[:,#]/.test(after))) return true;
    }
    return false;
  }

  // The draft to seed `q` with, or null. Once per field per page.
  function identityPrefill(q) {
    if (!q || !PREFILL_IDS[q.id] || state.prefilled[q.id]) return null;
    var src = prefillSource();
    if (!src) return null;
    state.prefilled[q.id] = true;
    if (q.id === 'q17') return src.name || null;
    if (q.id === 'q18') return src.birth || null;
    var hay = String(src.ethnicity || '').toLowerCase();
    var picked = [];
    (q.options || []).forEach(function (o) {
      if (hasToken(hay, String(o.value).toLowerCase()) || hasToken(hay, String(o.label).toLowerCase())) picked.push(o.value);
    });
    if (!picked.length) return null;
    return q.type === 'multiple-choice' ? picked : picked[0];
  }

  // --------------------------------------------- payload ports (submit.ts
  // buildSubmitRequestBody :255-314 non-guest path + its helpers)

  // Drop answers to questions that are hidden NOW. Going Back and changing an
  // earlier answer leaves the old follow-up answers in state.answers (kept so
  // they come back if the shopper switches back), e.g. Female + pregnant Yes,
  // then Male: q15 "Yes" would still be sent and set the pregnant metafield.
  // Repeat until stable so a follow-up of a dropped answer is dropped too;
  // removing an answer can only hide questions (evaluateRule is false on a
  // missing answer), so this ends. Ids not in the question list (e.g. q1 when
  // the Practice Code question is skipped) are kept as before.
  function omitHiddenAnswers(answers, questions) {
    var known = {};
    questions.forEach(function (q) { known[q.id] = true; });
    var kept = answers;
    for (;;) {
      var visible = {};
      getVisibleQuestions(questions, kept).forEach(function (q) { visible[q.id] = true; });
      var next = {};
      Object.keys(kept).forEach(function (questionId) {
        if (!known[questionId] || visible[questionId]) next[questionId] = kept[questionId];
      });
      if (Object.keys(next).length === Object.keys(kept).length) return next;
      kept = next;
    }
  }

  // submit.ts:231-245 — drop blank optional answers at the wire boundary only
  function omitBlankOptionalAnswers(answers, questions) {
    var effective = {};
    Object.keys(answers).forEach(function (questionId) {
      var value = answers[questionId];
      var question = null;
      for (var i = 0; i < questions.length; i++) {
        if (questions[i].id === questionId) { question = questions[i]; break; }
      }
      var isBlank = Array.isArray(value) ? value.length === 0 : value === '';
      if (question && question.required === false && isBlank) return;
      effective[questionId] = value;
    });
    return effective;
  }

  // lib/quiz-core/payload.ts:76-90
  function buildFormattedAnswers(answers, questions) {
    return Object.keys(answers).map(function (questionId) {
      var answer = answers[questionId];
      var question = null;
      for (var i = 0; i < questions.length; i++) {
        if (questions[i].id === questionId) { question = questions[i]; break; }
      }
      var category = (question && question.category) || 'demographics';
      return {
        questionId: questionId,
        answer: Array.isArray(answer) ? answer.join(', ') : answer,
        category: category,
        weight: 1
      };
    });
  }

  // lib/quiz-core/user-info.ts:45-89 (verbatim behavior)
  function deriveUserInfoFromAnswers(answers) {
    var derived = {};
    if ('q14' in answers) {
      var g = answers.q14;
      derived.gender = Array.isArray(g) ? g[0] : g;
    }
    if ('q16' in answers) {
      var e = answers.q16;
      derived.ethnicity = Array.isArray(e) ? e.join(', ') : e;
    }
    if ('q17' in answers) {
      var n = answers.q17;
      var names = (Array.isArray(n) ? n[0] : n).split(' ');
      derived.firstName = names[0] || '';
      derived.lastName = names.slice(1).join(' ') || '';
    }
    if ('q18' in answers) {
      var d = answers.q18;
      var pieces = (Array.isArray(d) ? d[0] : d).split('/');
      derived.birthMonth = pieces[0] || '';
      derived.birthYear = pieces[1] || '';
    }
    if ('q19' in answers) {
      var z = answers.q19;
      derived.zipCode = Array.isArray(z) ? z[0] : z;
    }
    return derived;
  }

  // submit.ts buildSubmitRequestBody (non-guest) — spec §1.3/§5 body shape
  function buildSubmitRequestBody(submitState) {
    var visibleAnswers = omitHiddenAnswers(submitState.answers, submitState.questions);
    var effectiveAnswers = omitBlankOptionalAnswers(visibleAnswers, submitState.questions);
    var formattedAnswers = buildFormattedAnswers(effectiveAnswers, submitState.questions);
    var derived = deriveUserInfoFromAnswers(effectiveAnswers);

    var userInfo = {};
    Object.keys(derived).forEach(function (k) { userInfo[k] = derived[k]; });
    userInfo.firstName = derived.firstName || '';
    userInfo.lastName = derived.lastName || '';
    userInfo.email = submitState.email;
    userInfo.marketingConsent = submitState.marketingConsent;
    // Precedence mirrors legacy assets/quiz.js: typed practice code (q1) wins,
    // then URL/stored code, then the storewide DTC default 'STSMD' — the
    // Lambda hard-requires an affiliateCode on every submission (400 otherwise).
    var q1 = submitState.answers && submitState.answers.q1;
    var typedCode = (Array.isArray(q1) ? q1[0] : q1 || '').trim().toUpperCase();
    userInfo.affiliateCode = typedCode || submitState.affiliateCode || 'STSMD';
    // Shopify's market country for the shopper (optional; backend stores it as market_country).
    var mcRoot = document.querySelector('[data-slq-root]');
    var mc = String((mcRoot && mcRoot.getAttribute('data-market-country')) || '').trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(mc)) userInfo.marketCountry = mc;

    var body = {
      answers: formattedAnswers,
      userInfo: userInfo
    };
    // truthy-only spreads, matching buildSubmitPayload exactly (empty string
    // honeypot / unset Turnstile token must be OMITTED, not falsy-present)
    if (submitState.turnstileToken) body.turnstileToken = submitState.turnstileToken;
    if (submitState.honeypot) body._hp = submitState.honeypot;
    body.context = 'widget';
    body.submissionId = submitState.submissionId;
    body.session_uuid = submitState.sessionUuid;
    body.assignments = assignments();
    body.configVersion = submitState.configVersion;
    return body;
  }

  // ---------------------------------------------------------------- app state

  var state = {
    view: 'landing',
    questions: [],
    questionsVersion: null,
    configVersionInt: 0,
    questionsStatus: 'idle', // idle | loading | ready | error
    // Where the question COPY came from, read off the payload envelope:
    //   'overlay' = the content service answered; published copy is merged in
    //   'static'  = the API could not reach it and served the shipped copy
    //   null      = this API build predates STS-Data and says nothing either way
    contentSource: null,
    contentRevision: null,
    whyWeAskServed: 0,
    answers: {},
    currentIndex: -1,
    draft: null,            // working value for the current question (string or string[])
    prefilled: {},          // question ids the signed-in prefill has already seeded (once each)
    accountAnswered: {},    // q17/q18 answered from the signed-in account, never shown (seedAccountAnswers)
    signedInSave: false,    // this submit went straight to the signed-in account (no email pop-up)
    checkingSession: false, // the last Continue is asking the store who is signed in now
    backDrafts: {},         // what was on screen when Back left a question uncommitted (memory only)
    trackedQuizStart: false,
    affiliateCode: null,
    q1Locked: false,       // q1 is showing the hidden 'code applied' confirmation, not an editable box
    email: '',
    marketingConsent: false,
    submitting: false,
    calcTimers: [],
    calcStartedAt: 0,
    calcStage: 0
  };

  // -------------------------------------------------------------- view switch

  // Fullscreen takeover: quiz/calculating/results hide EVERYTHING outside the
  // section (header, menus, footer, announcement bars, third-party popups) and
  // show only the fixed X. Implemented by hiding every sibling on the path
  // from the section root up to <body>, remembering each element's inline
  // display so exiting restores the page exactly. The X returns to landing.
  var chromeHidden = [];
  var takeoverObserver = null;

  // Third-party popups (Klaviyo forms, consent bars…) inject themselves as
  // new <body> children AFTER the takeover snapshot ran — watch for them and
  // hide them too while the takeover is active.
  /* Kept visible during the full-screen quiz: anything marked data-slq-keep,
     and the site's sign-in modal (#sts-auth, snippets/sts-auth-modal). The
     modal inerts everything outside itself, so hiding it here left a page
     nobody could click: "Sign in" from the quiz froze the screen (10-13). */
  function slqKeep(el) {
    if (!el || el.nodeType !== 1) return false;
    if (el.hasAttribute('data-slq-keep') || el.id === 'sts-auth') return true;
    return !!(el.querySelector && el.querySelector('#sts-auth'));
  }

  function watchLateChrome() {
    if (takeoverObserver || typeof MutationObserver === 'undefined') return;
    takeoverObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        for (var i = 0; i < m.addedNodes.length; i++) {
          var n = m.addedNodes[i];
          if (n.nodeType !== 1) continue;
          var tag = n.tagName;
          if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'LINK' || tag === 'NOSCRIPT' || tag === 'TEMPLATE') continue;
          if (slqKeep(n) || n.hasAttribute('data-slq-chrome-hidden')) continue;
          if (n.contains(root) || n === root) continue;
          n.setAttribute('data-slq-chrome-hidden', n.style.display || '');
          n.style.display = 'none';
          chromeHidden.push(n);
        }
      });
    });
    takeoverObserver.observe(document.body, { childList: true });
  }

  function unwatchLateChrome() {
    if (takeoverObserver) { takeoverObserver.disconnect(); takeoverObserver = null; }
  }

  function setTakeover(on) {
    var exitBtn = root ? root.querySelector('[data-slq-exit]') : null;
    if (exitBtn) exitBtn.toggleAttribute('hidden', !on);
    if (on) {
      if (chromeHidden.length) { watchLateChrome(); return; } // already on
      watchLateChrome();
      document.body.setAttribute('data-slq-takeover', '');
      var node = root;
      while (node && node.parentElement && node.tagName !== 'BODY') {
        var parent = node.parentElement;
        for (var i = 0; i < parent.children.length; i++) {
          var sib = parent.children[i];
          if (sib === node) continue;
          var tag = sib.tagName;
          if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'LINK' || tag === 'NOSCRIPT' || tag === 'TEMPLATE') continue;
          if (slqKeep(sib) || sib.hasAttribute('data-slq-chrome-hidden')) continue;
          sib.setAttribute('data-slq-chrome-hidden', sib.style.display || '');
          sib.style.display = 'none';
          chromeHidden.push(sib);
        }
        node = parent;
      }
    } else {
      unwatchLateChrome();
      document.body.removeAttribute('data-slq-takeover');
      chromeHidden.forEach(function (el) {
        el.style.display = el.getAttribute('data-slq-chrome-hidden') || '';
        el.removeAttribute('data-slq-chrome-hidden');
      });
      chromeHidden = [];
      /* The v4 header hides itself on scroll-down by carrying
         data-hidden="true" (translateY(-100%)) — see assets/sts-chrome.js and
         .sts-header[data-hidden] in assets/sts-chrome.css. display:none does
         not clear that attribute, so restoring the chrome can hand back a
         header that is translated off the top of the screen. Today go()'s
         scrollTo(0) usually clears it as a side effect, one scroll-up event
         later; putting the header back on screen HERE makes it part of
         restoring the chrome rather than a consequence of scrolling.
         sts-chrome.js takes over again on the next scroll. */
      var v4Header = document.querySelector('[data-sts-header]');
      if (v4Header) v4Header.setAttribute('data-hidden', 'false');
    }
  }

  function go(view) {
    state.view = view;
    $all('[data-slq-view]').forEach(function (el) {
      if (el.getAttribute('data-slq-view') === view) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });
    setTakeover(view !== 'landing');
    toTop();
  }
  /* A new screen lands at the top with no glide up from the last one (v6.2
     §1.3): theme-base.css sets html{scroll-behavior:smooth}, which a plain
     scrollTo follows. assets/sts-scroll.js makes it a jump. */
  function toTop() {
    var S = window.STS;
    if (S && S.scroll) S.scroll.top(); else window.scrollTo({ top: 0 });
  }
  SLQ.go = go;

  // The spec 02 §8 resume and retake toasts ("Back to question n of N...",
  // "The quiz opens at question 1...") are gone with every toast (v5.1
  // handoff 1.5): the quiz opening on that question is the confirmation.

  // ---------------------------------------------------------------- questions

  // The storewide DTC default. It is not a real practice code — it is what
  // every visitor who arrives without one gets, including via the site-wide
  // `affiliateCode` localStorage key that persists with no TTL. Rendering q1
  // for it produces a Continue-only dead click, so it is treated exactly like
  // "no code": q1 is dropped and submit applies the default anyway.
  var HOUSE_AFFILIATE_CODE = 'STSMD';

  // Single source of truth for whether q1 is part of this run's question set.
  // saveProgress/restoreProgress key off the SAME predicate, otherwise a
  // session saved on the real-code branch could restore onto the STSMD branch
  // (both have a truthy affiliateCode) and land currentIndex on the wrong
  // question.
  function showsPracticeCodeQuestion() {
    var code = (state.affiliateCode || '').trim();
    return !!code && code.toUpperCase() !== HOUSE_AFFILIATE_CODE;
  }

  function fetchQuestions() {
    if (state.questionsStatus === 'loading' || state.questionsStatus === 'ready') return Promise.resolve();
    state.questionsStatus = 'loading';
    renderQuizStatus();
    var url = stripTrailingSlash(SLQ.settings.nextBase) + '/api/quiz/questions';
    return netFetch(url, { method: 'GET' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!data || !Array.isArray(data.questions) || data.questions.length === 0) {
          throw new Error('empty questions payload');
        }
        state.questions = data.questions;
        // No code, or only the house default -> the STSMD path: the Practice
        // Code question is skipped entirely (submit already defaults to STSMD).
        // A real practice code still gets its confirmation screen.
        if (!showsPracticeCodeQuestion()) {
          state.questions = state.questions.filter(function (q) { return q.id !== 'q1'; });
        }
        state.questionsVersion = data.version;
        // configVersion: numeric version, else 0 (version "default" -> 0)
        var v = parseInt(data.version, 10);
        state.configVersionInt = isFinite(v) ? v : 0;
        // Dr. Baumann's published copy rides THIS payload — there is no second
        // request. Titles, help text and option labels are merged in place by
        // the API; the "why we ask this question" paragraphs arrive as an
        // additive per-question `whyWeAsk` array that renderQuestionInfo()
        // prefers over the QUESTION_INFO fallback below. These envelope fields
        // are what make the source observable at runtime instead of a guess:
        // 'static' means the copy on screen is the SHIPPED copy because the
        // content service was unreachable, which is not the same thing as
        // nothing having been published.
        state.contentSource = typeof data.contentSource === 'string' ? data.contentSource : null;
        state.contentRevision = typeof data.contentRevision === 'number' ? data.contentRevision : null;
        state.whyWeAskServed = 0;
        for (var wi = 0; wi < state.questions.length; wi++) {
          // `!== null` counts a deliberately-emptied explanation as served,
          // because it is: the storefront is showing her decision, not the
          // theme's fallback.
          if (servedWhyWeAsk(state.questions[wi]) !== null) state.whyWeAskServed++;
        }
        if (root) {
          root.setAttribute('data-slq-content-source', state.contentSource || 'unknown');
          root.setAttribute('data-slq-content-revision',
            state.contentRevision === null ? '' : String(state.contentRevision));
          root.setAttribute('data-slq-why-served', String(state.whyWeAskServed));
        }
        try {
          console.info('[SLQ] question copy: source=' + (state.contentSource || 'unknown')
            + ' revision=' + (state.contentRevision === null ? 'n/a' : state.contentRevision)
            + ' - why-we-ask served for ' + state.whyWeAskServed + ' of '
            + state.questions.length + ' question(s); the rest use the theme fallback.');
        } catch (e) { /* logging is advisory only */ }
        state.questionsStatus = 'ready';
        restoreProgress();
        if (state.view === 'quiz') startQuestionFlow();
      })
      .catch(function (err) {
        state.questionsStatus = 'error';
        state.questionsError = (err && err.message) ? err.message : 'network error';
        renderQuizStatus();
      });
  }

  // ------------------------------------------------------- progress persist
  // Mid-quiz reloads must never lose answers: every commit checkpoints
  // {answers, currentIndex} to sessionStorage (per-tab). Cleared on a
  // successful submit; a version mismatch discards stale progress.
  var PROGRESS_KEY = 'slqQuizProgress';

  function saveProgress() {
    try {
      sessionStorage.setItem(PROGRESS_KEY, JSON.stringify({
        answers: state.answers,
        currentIndex: state.currentIndex,
        version: state.questionsVersion,
        hadCode: showsPracticeCodeQuestion()
      }));
    } catch (e) { /* noop */ }
  }

  function clearProgress() {
    try { sessionStorage.removeItem(PROGRESS_KEY); } catch (e) { /* noop */ }
  }

  function restoreProgress() {
    try {
      var saved = JSON.parse(sessionStorage.getItem(PROGRESS_KEY) || 'null');
      if (!saved || !saved.answers || saved.version !== state.questionsVersion) return false;
      if (saved.hadCode !== showsPracticeCodeQuestion()) return false; // q1 presence differs
      state.answers = saved.answers;
      var idx = parseInt(saved.currentIndex, 10);
      if (isFinite(idx) && idx >= 0 && idx < state.questions.length) state.currentIndex = idx;
      // Resume AT the first unanswered question, never the last one viewed: a
      // visitor who went Back to Q2 with three answers resumes on Q4. It also
      // means a checkpoint whose index outruns its answers rewinds to the gap
      // instead of walking to the gate with a partial set.
      return resumeAtFirstUnanswered();
    } catch (e) { return false; }
  }

  function hasSavedProgress() {
    try {
      var saved = JSON.parse(sessionStorage.getItem(PROGRESS_KEY) || 'null');
      return !!(saved && saved.answers && Object.keys(saved.answers).length > 0);
    } catch (e) { return false; }
  }

  function renderQuizStatus() {
    var loading = $('[data-slq-quiz-loading]');
    var error = $('[data-slq-quiz-error]');
    var main = $('[data-slq-quiz-main]');
    if (!loading || !error || !main) return;
    loading.toggleAttribute('hidden', state.questionsStatus !== 'loading');
    error.toggleAttribute('hidden', state.questionsStatus !== 'error');
    main.toggleAttribute('hidden', state.questionsStatus !== 'ready');
    if (state.questionsStatus === 'error') {
      var msg = $('[data-slq-quiz-error-msg]');
      /* state.questionsError is for debugging; the raw exception ("Failed to
         fetch") is never printed to the shopper. */
      if (msg) msg.textContent = 'The questionnaire could not be loaded. Check your connection and try again.';
    }
  }

  // ------------------------------------------------------------- quiz engine

  function startQuiz() {
    go('quiz');
    if (state.questionsStatus === 'ready') {
      // back in from the landing (the x, then the CTA): same rule as a reload
      if (hasSavedProgress()) resumeAtFirstUnanswered();
      startQuestionFlow();
    } else {
      fetchQuestions();
    }
  }

  function startQuestionFlow() {
    if (inviteGate()) return;
    renderQuizStatus();
    seedAccountAnswers();
    if (state.currentIndex === -1) {
      var first = getNextVisibleQuestion(state.questions, -1, state.answers, 'next');
      state.currentIndex = first;
    } else if (accountAnswered(currentQuestion())) {
      // a resume that lands on the name or birth date the account just answered
      var past = getNextVisibleQuestion(state.questions, state.currentIndex, state.answers, 'next');
      if (past === -1) { saveProgress(); finishQuestions(); return; }
      state.currentIndex = past;
    }
    // "started" goes out BEFORE the first question's quiz_question_loaded so
    // GA4 funnels read visited -> started -> question -> ... -> completed.
    if (!state.trackedQuizStart) gaEvent('started');
    renderQuestion();
    if (!state.trackedQuizStart) {
      state.trackedQuizStart = true;
      SLQ.track('quiz_start', {
        content_source: state.contentSource || 'unknown',
        content_revision: state.contentRevision,
        why_we_ask_served: state.whyWeAskServed,
        // First-touch source (assets/sts-track.js); absent = "not captured".
        traffic: window.STSTrack && STSTrack.firstTouch ? STSTrack.firstTouch() || undefined : undefined
      });
    }
  }

  function currentQuestion() {
    return state.questions[state.currentIndex] || null;
  }

  function renderQuestion() {
    var q = currentQuestion();
    if (!q) return;
    state.draft = ('' + q.id) in state.answers ? state.answers[q.id] : (q.type === 'multiple-choice' ? [] : '');
    /* Back keeps what the customer left on screen (typed, edited, cleared or
       prefilled): only Continue commits an answer, so a birth date entered and
       then left with Back came back empty. Restored HERE, at build time, into
       the draft the field below is built from; the field is new, never focused
       or typed in. Consumed once. Else the signed-in prefill seeds the draft. */
    if (Object.prototype.hasOwnProperty.call(state.backDrafts, q.id)) {
      state.draft = state.backDrafts[q.id];
      delete state.backDrafts[q.id];
    } else if (!(('' + q.id) in state.answers)) {
      var pre = identityPrefill(q);
      if (pre !== null) state.draft = pre;
    }

    // progress: position within currently-visible questions (the name and
    // birth date a signed-in account answered are never shown, so not counted)
    var visible = getVisibleQuestions(state.questions, state.answers).filter(function (v) {
      return !accountAnswered(v);
    });
    var skippedByAccount = Object.keys(state.accountAnswered).filter(function (id) {
      for (var j = 0; j < state.questions.length; j++) {
        if (state.questions[j].id === id) return accountAnswered(state.questions[j]);
      }
      return false;
    }).length;
    var pos = 0;
    for (var i = 0; i < visible.length; i++) {
      if (visible[i].id === q.id) { pos = i + 1; break; }
    }
    var total = Math.max(possibleQuestionCount(state.questions, state.answers) - skippedByAccount, visible.length, 1);
    var bar = $('[data-slq-progress-bar]');
    if (bar) bar.style.width = Math.round((Math.max(pos - 1, 0) / total) * 100) + '%';
    var count = $('[data-slq-progress-count]');
    if (count) count.textContent = 'Question ' + pos + ' of ' + total;

    var qtext = $('[data-slq-qtext]');
    if (qtext) qtext.textContent = q.text || '';
    gaEvent('questionLoaded', gaQuestionFields(q));
    // internal telemetry twin of the GA event: code only, never answer text —
    // feeds the evidence pack's per-question completion digest
    SLQ.track('question_viewed', { question_code: q.id, question_type: q.type });
    var qhelp = $('[data-slq-qhelp]');
    var hasHelp = false;
    if (qhelp) {
      if (q.helpText) { qhelp.textContent = q.helpText; qhelp.removeAttribute('hidden'); hasHelp = true; }
      else { qhelp.textContent = ''; qhelp.setAttribute('hidden', ''); }
    }
    hideError();
    renderQuestionInfo(q);

    var body = $('[data-slq-qbody]');
    if (!body) return;
    // Name the option group after the question, plus the help text when it is
    // showing — that is where "check all that are true" / "choose one" lives, so
    // without it a screen-reader user is never told the question is multi-select.
    // A hidden element must NOT stay in aria-labelledby or it is read anyway.
    body.setAttribute('aria-labelledby', hasHelp ? 'slq-qtext slq-qhelp' : 'slq-qtext');
    body.innerHTML = '';

    if (q.type === 'single-choice' || q.type === 'multiple-choice') {
      renderChoiceOptions(q, body);
    } else if (q.type === 'textarea') {
      renderTextInput(q, body, true);
    } else {
      renderTextInput(q, body, false);
    }

    applyQuestionCustomizations(q, body);

    /* Every question type ends in the same Continue button (CTO, 2026-10-13:
       no auto-advance, a mis-click must be correctable; be consistent).
       A tap on a single-choice option only SELECTS it; nothing advances until
       Continue is pressed. On a choice question Continue stays disabled until
       an answer is picked; text questions keep their own validation messages. */
    var cont = $('[data-slq-continue]');
    if (cont) {
      cont.removeAttribute('hidden');
      // One label everywhere: the served buttonLabel is the legacy all-caps
      // "CONTINUE" on q1/q17/q18, so only a genuinely different label is kept.
      var label = String(q.buttonLabel || '').trim();
      cont.textContent = (!label || /^continue$/i.test(label)) ? 'Continue' : label;
    }
    updateContinueState(q);
    // ZIP is optional and US-only; Skip lets international takers past it.
    var skip = $('[data-slq-skip]');
    if (skip) skip.toggleAttribute('hidden', q.id !== 'q19');

    var back = $('[data-slq-back]');
    if (back) {
      var prev = getNextVisibleQuestion(state.questions, state.currentIndex, state.answers, 'previous');
      back.toggleAttribute('hidden', prev === -1);
    }

    // Move focus to the new question heading. Without this the DOM swaps
    // underneath a keyboard/screen-reader user while focus stays parked on the
    // Continue button they just pressed — the question silently changes and
    // nothing is announced. tabindex="-1" makes the heading programmatically
    // focusable without adding it to the tab order. Skipped on the very first
    // paint so opening the quiz doesn't yank focus mid-scroll.
    if (qtext && state.hasRenderedAQuestion) {
      qtext.setAttribute('tabindex', '-1');
      try { qtext.focus({ preventScroll: true }); } catch (e) { qtext.focus(); }
    }
    state.hasRenderedAQuestion = true;
  }

  /* FALLBACK "Why we ask this" copy, transcribed verbatim from Dr. Baumann's
     "Quiz text 2026" brief. Keyed by question id, value is an array of
     paragraphs.

     THIS MAP IS NO LONGER THE SOURCE OF TRUTH. renderQuestionInfo() prefers
     `question.whyWeAsk`, served by /api/quiz/questions, which carries whatever
     Dr. Baumann published in STS-Data. The map renders only when nothing is
     published for that question, when the API build predates the overlay, or
     when the content service was unreachable — it is the safety net that keeps
     the explanations on screen during an outage, not the copy she edits.
     Editing it changes live copy only until something is published for that
     question id, at which point the published text wins. Publish instead.

     Scope note: the brief ALSO proposes rewrites of several question stems and
     help texts. Those are deliberately NOT applied here — this change adds the
     explanations only and leaves every question and option exactly as it is.

     Questions with no entry render no toggle at all. The brief marks the two
     concerns questions (q9 / q9-2), both sunscreen questions (q11 / q12) and
     the background question (q16) as "lets discuss", so they have no approved
     copy yet and are intentionally absent. */
  var QUESTION_INFO = {
    // Q1 — moisturization
    'q3': [
      'How often your skin needs moisturizer and how it reacts to soap give us clues about your skin barrier and natural oil production. This helps us choose the right cleansers and moisturizers for you.'
    ],
    // Q2 — sebum production
    'q4': [
      'How oily or dry your skin seems is only one clue to how much sebum it makes. These answers help us estimate your sebum production and which ingredients to include or avoid throughout your routine, from cleansers and serums to moisturizers and sunscreens.'
    ],
    // Q3 — underlying inflammation
    'q5': [
      'Reactive skin has many causes, and you may have more than one cause of inflammation at the same time. These answers help uncover which of the four sensitive skin types you have, and you may have several. When sensitivities overlap, choosing products becomes more complicated because what helps one concern may aggravate another. That’s where a skin-type-specific routine really matters.',
      'If products cause pimples, redness, burning, or stinging, don’t choose “allergic reactions.” Instead, choose the symptom you experience. Choose “allergic reactions” only if you’ve had a recurring reaction to a skincare product or ingredient, such as an itchy, red, scaling rash or hives.',
      'If you tell us that the smell of fragrances bothers you, we will choose products that do not have a strong smell. If you are fragrance allergic, choose the allergic option and the “The smell of fragrance bothers me” option.'
    ],
    // Q4 — pigmentation / skin lighteners
    'q6': [
      'Your answer determines whether our personalized skincare recommendations target dark spots and uneven pigmentation in your routine. Inflammation experienced by the four sensitive skin types can trigger or worsen pigmentation. Sometimes we need to control inflammation or repair the skin barrier before aggressively treating dark spots.',
      'Your Baumann Skin Type helps us decide not only which products to use, but which skin concerns to treat first and how to layer your routine so the products work well together.'
    ],
    // Q5 — lifestyle habits
    'q7': [
      'Age matters, but daily habits also determine whether your skin is wrinkle-prone. A W-type predicts future aging risk, even if you’re young and wrinkle-free today.',
      'Your answers help us personalize antioxidants, retinoids, and other anti-aging ingredients to counteract the aging effects of your lifestyle while addressing your other skin concerns. We consider your entire skin type: Will a treatment irritate sensitive skin, weaken a fragile skin barrier, or work well on oily skin?',
      'This is where many people go wrong when building a routine without a dermatologist’s input: they treat one concern without considering how each product affects their other skin concerns.'
    ],
    // Q6 — suncare habits
    'q7-2': [
      'UV exposure affects more than aging. Sun damage can weaken your skin barrier, trigger inflammation and dark spots, accelerate skin aging, and increase skin cancer risk.',
      'Your answers help us assess your sun exposure and personalize your sunscreen, antioxidants, and other protective and repair treatments.',
      'Answer honestly about your past and current sun habits. It’s never too late to protect your skin and start improving its health. Coming up, we’ll ask about your sunscreen preferences so we can help you find a sunscreen you’ll actually want to wear every day.'
    ],
    // Q7 — eye product
    'q8': [
      'The delicate skin around your eyes has different needs than the rest of your face, and some facial products may irritate the eye area or sting your eyes.',
      'Tell us what bothers you most. We’ll prioritize your eye concerns and help choose the right cream, gel, or serum to target them, rather than expecting one eye product to do everything.'
    ],
    // Q12 — gender
    'q14': [
      'Hormones can affect oiliness, breakouts, pigmentation, and other skin concerns. Your answer also helps us personalize details such as fragrance preferences, so the products we recommend fit both your skin and your preferences.'
    ],
    // Q13 — pregnancy / breastfeeding
    'q15': [
      'Your safety comes first. Pregnancy and breastfeeding affect which skincare ingredients we recommend. We’ll adjust your routine to avoid ingredients such as retinoids and high concentrations of salicylic acid and choose appropriate alternatives for your skin concerns.',
      'Always discuss new skincare products with your doctor during pregnancy or breastfeeding, especially products applied over large areas of the body, such as body creams and lotions.'
    ]
  };

  /* The published paragraphs for one question, or null if none were published.

     ⚠️ An EMPTY ARRAY is a published answer, not the absence of one. It means
     an editor opened the paragraph boxes and left them empty — she deleted the
     explanation on purpose — so it returns [] and the caller renders nothing.
     Only a MISSING or non-array `whyWeAsk` returns null and falls through to
     the QUESTION_INFO map below.

     This returned `null` for [] once, on the reasoning that the caller could
     then use a plain `||`. The cost was that a deletion could never take
     effect: the publish succeeded, the field arrived as [], this collapsed it
     to null, and the theme's own hardcoded paragraph reappeared as though it
     were current copy. The API half of the same contract is the `why` branch of
     lib/quiz/content-overlay.ts in skin-type-pro, which must emit `whyWeAsk: []`
     rather than omitting the field. Changing one half alone restores the bug.

     Still defensive about the rest: a non-string or blank entry inside the
     array is dropped, so a stray "" cannot open a panel that promises an
     explanation and then shows nothing. */
  function servedWhyWeAsk(q) {
    var raw = q && q.whyWeAsk;
    if (!Array.isArray(raw)) return null;
    var paras = [];
    for (var i = 0; i < raw.length; i++) {
      if (typeof raw[i] === 'string' && raw[i].trim()) paras.push(raw[i]);
    }
    return paras;
  }

  function renderQuestionInfo(q) {
    var wrap = $('[data-slq-qinfo]');
    if (!wrap) return;
    var served = servedWhyWeAsk(q);
    // `!== null`, NEVER a plain `||`: served is [] when the explanation was
    // deleted on purpose, and `[] || fallback` yields [] only by the accident
    // that an empty array is truthy. Saying it explicitly keeps a later reader
    // from "simplifying" the deletion back out of existence.
    var paras = served !== null ? served : (q && QUESTION_INFO[q.id]);
    // Which source rendered is readable straight off the DOM, so confirming
    // that a publish reached the live storefront is one attribute check and
    // not a diff of two paragraphs by eye. 'published' with the panel hidden is
    // a deliberate deletion; 'none' is a question nobody ever wrote copy for.
    wrap.setAttribute('data-slq-qinfo-source',
      served !== null ? 'published' : ((paras && paras.length) ? 'theme' : 'none'));
    if (!paras || !paras.length) { wrap.setAttribute('hidden', ''); return; }

    var toggle = $('[data-slq-qinfo-toggle]');
    var panel = $('[data-slq-qinfo-panel]');
    if (!toggle || !panel) return;

    panel.innerHTML = '';
    paras.forEach(function (text, i) {
      var p = document.createElement('p');
      p.textContent = text;
      if (i) p.style.marginTop = '9px';
      panel.appendChild(p);
    });

    // Always collapse on question change — an explanation left open from the
    // previous question reads as if it belongs to this one.
    panel.setAttribute('hidden', '');
    toggle.setAttribute('aria-expanded', 'false');
    wrap.removeAttribute('hidden');

    if (!toggle.__slqBound) {
      toggle.__slqBound = true;
      toggle.addEventListener('click', function () {
        var open = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
        panel.toggleAttribute('hidden', open);
      });
    }
  }

  function optionLetter(i) {
    return String.fromCharCode(65 + (i % 26));
  }

  function renderChoiceOptions(q, body) {
    var isMulti = q.type === 'multiple-choice';
    q.options.forEach(function (opt, i) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'slq-opt';
      btn.setAttribute('data-slq-opt-value', opt.value);
      // Default the toggle state so every option exposes it from first paint —
      // markSelected() flips it, but an unselected option must still announce
      // itself as a toggle rather than a plain button.
      btn.setAttribute('aria-pressed', 'false');
      btn.style.cssText = 'border:1.5px solid #DBDCDD;background:#fff;border-radius:14px;padding:12px 15px;font-size:15px;font-weight:500;color:#26284a;display:flex;align-items:center;gap:12px;cursor:pointer;font-family:inherit;text-align:left;width:100%';
      var key = document.createElement('span');
      key.style.cssText = 'border-radius:50%;min-width:28px;height:28px;background:#DBDCDD;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;color:#2F3572;flex:none';
      key.textContent = optionLetter(i);
      btn.appendChild(key);
      btn.appendChild(document.createTextNode(opt.label));
      if (isMulti) {
        if (Array.isArray(state.draft) && state.draft.indexOf(opt.value) !== -1) markSelected(btn, true);
        btn.addEventListener('click', function () { if (!continueGuarded()) toggleMultiOption(q, btn, opt); });
      } else {
        if (state.draft === opt.value) markSelected(btn, true);
        btn.addEventListener('click', function () { if (!continueGuarded()) pickSingleOption(q, btn, opt); });
      }
      // Keyboard: Enter on an option that is already selected = Continue
      // (Space, or Enter on an unselected option, still selects/toggles it).
      btn.addEventListener('keydown', function (ev) {
        if (ev.key !== 'Enter' || btn.getAttribute('aria-pressed') !== 'true') return;
        ev.preventDefault();
        onContinue();
      });
      body.appendChild(btn);
    });
  }

  function markSelected(btn, on) {
    // aria-pressed is the ONLY selection signal a screen reader gets: the
    // selected state is otherwise conveyed purely by border/background colour,
    // so without this a non-sighted user cannot tell which options they picked
    // (and on a multi-select question that is the whole answer).
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    if (on) {
      btn.classList.add('slq-opt--selected');
      btn.style.borderColor = 'var(--slq-type-deep)';
      btn.style.background = 'var(--slq-type-tint)';
    } else {
      btn.classList.remove('slq-opt--selected');
      btn.style.borderColor = '#DBDCDD';
      btn.style.background = '#fff';
    }
  }

  // A tap only selects (tapping another option moves the selection). The
  // answer is committed by Continue -> onContinue, exactly like multi-choice,
  // so a mis-tap can be corrected before it moves the quiz on.
  function pickSingleOption(q, btn, opt) {
    $all('.slq-opt').forEach(function (el) { markSelected(el, false); });
    markSelected(btn, true);
    state.draft = opt.value;
    hideError();
    updateContinueState(q);
  }

  // Continue is disabled on a choice question until it has a valid answer
  // (an optional multi-choice question is always valid). Text questions are left
  // enabled so their specific error messages still show on Continue.
  function updateContinueState(q) {
    var cont = $('[data-slq-continue]');
    if (!cont || !q) return;
    var isChoice = q.type === 'single-choice' || q.type === 'multiple-choice';
    // A single-choice question always needs a pick, even an optional one:
    // before Continue it could only be left by tapping an answer, and an empty
    // single-choice answer would be a silent new state for the payload.
    var off = isChoice && (q.type === 'single-choice'
      ? !(typeof state.draft === 'string' && state.draft !== '')
      : !validateAnswer(q, state.draft));
    cont.disabled = off;
    if (off) cont.setAttribute('aria-disabled', 'true');
    else cont.removeAttribute('aria-disabled');
  }

  // Repeat-press guard: a double tap on Continue must not also answer, skip or
  // go Back from the question that replaces this one (the next question has a
  // different height, so the second tap can land on an option or on Back).
  // For a moment after a move it IGNORES presses on Continue, Skip, Back and
  // the options; it never cancels anything, so a re-render cannot stall it.
  var CONTINUE_GUARD_MS = 400;
  function continueGuarded() {
    return Date.now() < (state.continueLockUntil || 0);
  }

  function toggleMultiOption(q, btn, opt) {
    if (!Array.isArray(state.draft)) state.draft = [];
    var idx = state.draft.indexOf(opt.value);
    if (idx === -1) {
      if (q.maxSelections && state.draft.length >= q.maxSelections) {
        showError('You can select up to ' + q.maxSelections + ' options.');
        shake();
        return;
      }
      state.draft.push(opt.value);
      markSelected(btn, true);
      // "None of the above" exclusivity (quiz.js handleMultipleChoice:560-607):
      // picking None clears every other selection; picking anything else
      // clears None.
      var NONE = 'None of the above';
      var isNone = (opt.label || '').indexOf(NONE) !== -1;
      var body = btn.parentElement;
      if (body) {
        var buttons = body.querySelectorAll('.slq-opt');
        buttons.forEach(function (other) {
          if (other === btn) return;
          var otherIsNone = other.textContent.indexOf(NONE) !== -1;
          if ((isNone && !otherIsNone) || (!isNone && otherIsNone)) {
            var val = other.getAttribute('data-slq-opt-value');
            var vi = state.draft.indexOf(val);
            if (vi !== -1) state.draft.splice(vi, 1);
            markSelected(other, false);
          }
        });
      }
    } else {
      state.draft.splice(idx, 1);
      markSelected(btn, false);
    }
    hideError();
    updateContinueState(q);
  }

  function renderTextInput(q, body, isTextarea) {
    var input = document.createElement(isTextarea ? 'textarea' : 'input');
    if (!isTextarea) input.type = 'text';
    input.setAttribute('data-slq-text-input', '');
    input.placeholder = q.placeholder || '';
    input.value = typeof state.draft === 'string' ? state.draft : '';
    // font-size held at the 16px iOS floor — quiz text questions (e.g. birthdate)
    // render through here, and under 16px Safari zooms the whole page on focus.
    input.style.cssText = 'width:100%;border:1.5px solid #DBDCDD;border-radius:12px;padding:14px 16px;font-size:16px;font-family:inherit;color:#26284a;background:#fff' + (isTextarea ? ';min-height:110px;resize:vertical' : '');
    input.addEventListener('input', function () { state.draft = input.value; hideError(); });
    if (!isTextarea) {
      input.addEventListener('keydown', function (ev) {
        if (ev.key === 'Enter') { ev.preventDefault(); onContinue(); }
      });
    }
    // MM/YYYY affordance for the birthdate question
    if (q.validationPattern === 'MM/YYYY') {
      input.setAttribute('inputmode', 'numeric');
      input.setAttribute('maxlength', '7');
      if (!input.getAttribute('placeholder')) input.setAttribute('placeholder', 'MM/YYYY');
      /* Type digits only — the slash is inserted for you.
         A numeric keyboard has no "/" key, so requiring the visitor to type one
         made this question impossible to answer on a phone. */
      input.addEventListener('input', function (ev) {
        var deleting = !!(ev && typeof ev.inputType === 'string' && ev.inputType.indexOf('delete') === 0);
        var next = formatMonthYear(input.value, deleting);
        if (next !== input.value) {
          input.value = next;
          try { input.setSelectionRange(next.length, next.length); } catch (e) { /* not all inputs allow it */ }
        }
        // The generic handler above already ran and stored the UNFORMATTED
        // value, so re-sync the draft to what is actually on screen.
        state.draft = input.value;
      });
    }
    body.appendChild(input);
    window.setTimeout(function () { try { input.focus(); } catch (e) { /* noop */ } }, 50);
  }

  function showError(msg) {
    var el = $('[data-slq-qerror]');
    if (!el) return;
    el.textContent = msg;
    el.removeAttribute('hidden');
  }

  function hideError() {
    var el = $('[data-slq-qerror]');
    if (!el) return;
    el.setAttribute('hidden', '');
  }

  function shake() {
    var card = $('[data-slq-qcard]');
    if (!card) return;
    card.style.animation = 'none';
    void card.offsetWidth; // restart animation
    card.style.animation = 'slq-shake .4s ease';
  }

  // Continue for every question type (single, multi, text, textarea)
  /* Practice-code check (q1).
   *
   * An unrecognised code used to sail through all 18 questions and only blow up
   * at submit: the scoring Lambda cannot resolve it, so the taker got a bare
   * 500, no results, and retrying failed identically. Catching it here is the
   * difference between "fix this one field" and "your quiz is gone".
   *
   * FAILS OPEN. If the check itself errors or times out, the visitor continues —
   * an outage in the validator must never block the funnel; the worst case is
   * the old behaviour.
   */
  var practiceCodeCache = {};
  var practiceCodeInFlight = false;

  function checkPracticeCode(code, done) {
    var key = code.toUpperCase();
    if (practiceCodeCache[key]) { done(practiceCodeCache[key]); return; }
    if (practiceCodeInFlight) return;
    practiceCodeInFlight = true;

    var cont = $('[data-slq-continue]');
    var restore = cont ? cont.textContent : null;
    if (cont) { cont.disabled = true; cont.textContent = 'Checking…'; }
    var settle = function (verdict) {
      practiceCodeInFlight = false;
      if (cont) { cont.disabled = false; if (restore !== null) cont.textContent = restore; }
      if (verdict !== 'unknown') practiceCodeCache[key] = verdict;
      done(verdict);
    };

    var url = stripTrailingSlash(SLQ.settings.nextBase) +
      '/api/quiz/validate-affiliate?code=' + encodeURIComponent(code);
    var timer = window.setTimeout(function () { settle('unknown'); }, 6000);

    netFetch(url, { method: 'GET' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        window.clearTimeout(timer);
        if (!data || typeof data.isValid !== 'boolean') { settle('unknown'); return; }
        settle(data.isValid ? 'valid' : 'invalid');
      })
      .catch(function () { window.clearTimeout(timer); settle('unknown'); });
  }

  // Skip on the ZIP step: same as a blank Continue (stored '' is dropped at submit).
  function onSkip() {
    if (continueGuarded()) return;
    var q = currentQuestion();
    if (!q || q.id !== 'q19') return;
    state.draft = '';
    state.answers[q.id] = '';
    saveProgress();
    advance();
  }

  function onContinue() {
    if (continueGuarded()) return;
    var q = currentQuestion();
    if (!q) return;
    var value = state.draft;
    if (q.type === 'multiple-choice' && !Array.isArray(value)) value = [];

    // validation ports
    if (q.id === 'q1') {
      var codeRaw = (Array.isArray(value) ? (value[0] || '') : (value || '')).trim();
      if (state.q1Locked && state.affiliateCode) {
        // Confirmation screen for a known practice code: it arrived in ?code=
        // (or storage) and there is no box the visitor could correct. Still
        // check it, because affiliateNames.json carries codes that no longer
        // exist in the backend (40 of 246 on 2026-10-02 match no row in
        // affiliate, companies or company_locations). Sending one of those on
        // makes the scoring Lambda throw and the taker gets no results at all,
        // so a definitive rejection drops quietly to the house default --
        // which is what happens today -- instead of showing an error on a
        // hidden field. Anything inconclusive keeps the code: that is the
        // whole point of the fix.
        var lockedCode = state.affiliateCode;
        checkPracticeCode(lockedCode, function (verdict) {
          if (verdict === 'invalid') {
            state.affiliateCode = '';
            state.answers[q.id] = '';
          } else {
            state.answers[q.id] = lockedCode;
          }
          saveProgress();
          advance();
        });
        return;
      }
      if (!codeRaw) {
        // Cleared on purpose. Drop the ARRIVING code too: submit resolves
        // `typed || stored || STSMD`, so leaving state.affiliateCode set would
        // quietly re-use the very code the visitor just erased — which is the
        // unrecognised one that breaks scoring.
        state.affiliateCode = '';
        state.answers[q.id] = '';
        saveProgress();
        advance();
        return;
      }
      checkPracticeCode(codeRaw, function (verdict) {
        if (verdict === 'invalid' || !AFF_RE.test(codeRaw)) {
          showError('We don’t recognise that practice code. Check it with your provider, or clear the box to continue without one.');
          shake();
          return;
        }
        state.answers[q.id] = codeRaw;
        // The code was accepted, so it is now THE code. Without this every
        // mirror keeps whatever arrived in ?code= — submit still sends the
        // typed one, but the storefront chrome, the 'Practice code applied'
        // note and any downstream localStorage reader stay on the old code.
        // Correcting one valid practice to another would otherwise leave the
        // visitor attributed two different ways at once.
        state.affiliateCode = codeRaw;
        lsSet('affiliateCode', codeRaw);
        ssSet('affiliateCode', codeRaw);   // ssGet wins in resolveAffiliateCode
        try { document.body.setAttribute('data-affiliate-code', codeRaw); } catch (e) { /* noop */ }
        saveProgress();
        advance();
      });
      return;
    }
    if (q.id === 'q17') {
      var nameErr = validateName(Array.isArray(value) ? (value[0] || '') : (value || ''));
      if (nameErr) { showError(nameErr); shake(); return; }
    } else if (!validateAnswer(q, value)) {
      showError('This question is required.');
      shake();
      return;
    }
    if (q.validationPattern === 'MM/YYYY') {
      var raw = (Array.isArray(value) ? (value[0] || '') : (value || '')).trim();
      if (raw && !isValidMonthYear(raw)) {
        showError('Please enter your birth date as MM/YYYY (for example 04/1985).');
        shake();
        return;
      }
      value = raw;
    }
    // ZIP: optional, but when provided it must be a real US ZIP (5 or 5+4).
    if (q.id === 'q19') {
      var zipRaw = (Array.isArray(value) ? (value[0] || '') : (value || '')).trim();
      if (zipRaw && !/^\d{5}(-\d{4})?$/.test(zipRaw)) {
        showError('Please enter a valid ZIP code (for example 33131).');
        shake();
        return;
      }
      value = zipRaw;
    }
    state.answers[q.id] = value;
    saveProgress();
    advance();
  }

  function advance() {
    var answered = currentQuestion();
    if (answered) SLQ.track('question_answered', { question_code: answered.id, question_type: answered.type });
    if (answered && (answered.type === 'single-choice' || answered.type === 'multiple-choice')) {
      var v = state.answers[answered.id];
      var f = gaQuestionFields(answered);
      f.question_value = Array.isArray(v) ? v.join(', ') : (typeof v === 'string' ? v : undefined);
      gaEvent('questionAnswered', f);
    }
    state.continueLockUntil = Date.now() + CONTINUE_GUARD_MS;
    var next = getNextVisibleQuestion(state.questions, state.currentIndex, state.answers, 'next');
    if (next === -1) {
      saveProgress();
      finishQuestions();
      return;
    }
    state.currentIndex = next;
    saveProgress(); // checkpoint AFTER moving so a reload resumes on the next question
    renderQuestion();
    toTop();
  }

  function goBack() {
    if (continueGuarded()) return;
    var prev = getNextVisibleQuestion(state.questions, state.currentIndex, state.answers, 'previous');
    if (prev === -1) return;
    // state.draft is what is on screen; for MM/YYYY it is the already
    // formatted value (renderTextInput re-syncs it after the slash pass).
    var left = currentQuestion();
    if (left && state.draft != null) state.backDrafts[left.id] = Array.isArray(state.draft) ? state.draft.slice() : state.draft;
    state.currentIndex = prev;
    state.continueLockUntil = Date.now() + CONTINUE_GUARD_MS;
    saveProgress();
    renderQuestion();
    toTop();
  }

  // ---------------------------------------------------------------- email gate

  var turnstileWidgetId = null;
  var turnstileLoading = false;
  var turnstilePending = null; // resolver for an in-flight token refresh

  function openGate() {
    var gate = $('[data-slq-gate]');
    if (!gate) return;
    gate.removeAttribute('hidden');
    ensureTurnstile();
    var email = $('[data-slq-gate-email]');
    if (email) {
      if (state.email) email.value = state.email;
      else {
        // Prefill: the logged-in customer (rendered by Liquid, authoritative),
        // else the returning-taker hint (quiz.js setQuizInputs:380) — read-only.
        var storedEmail = gateLoggedInEmail() || lsGet('customerEmail');
        if (storedEmail && !email.value) email.value = storedEmail;
      }
      scheduleGateEmailCheck(true);
      window.setTimeout(function () { try { email.focus(); } catch (e) { /* noop */ } }, 80);
    }
  }

  /* The last question is answered. A guest gets the email pop-up. A signed-in
     customer does not (Dr. Baumann, 2026-10-13: it asked for her email, and
     its "Skip" sent a signed-in retake as a guest, so the account kept its
     old type while this browser showed the new one). Their results go
     straight to the account email, which the App Proxy matches against
     Shopify's signed customer id before anything is saved to the account. */
  function finishQuestions() {
    if (state.checkingSession || state.submitting) return;
    state.checkingSession = true;
    var before = gateLoggedInEmail();
    signedInEmailNow().then(function (now) {
      state.checkingSession = false;
      if (now === null) now = before; // could not check: keep what the page loaded with
      if (now !== before) {
        root.setAttribute('data-customer-email', now);
        SLQ.track('quiz_signin_changed', { signedIn: !!now });
      }
      if (now) { submitSignedIn(); return; }
      // Signed out while the quiz was open: the pop-up starts with their email.
      if (before && !state.email) state.email = before;
      openGate();
    });
  }

  /* Who is signed in RIGHT NOW (Luis, 2026-10-13: the sign-in must hold for
     the whole quiz). The page reads it once, at load; this asks again at the
     last Continue by fetching this page fresh (same origin, so the store's
     own sign-in cookie decides) and reading data-customer-email. A sign-in
     made in another tab during the quiz then counts, and a page that went
     stale never sends a signed-out shopper down the signed-in path.
     '' = signed out, null = could not tell (network, timeout). */
  function signedInEmailNow() {
    var url = window.location.pathname + '?sts_signin_check=' + Date.now();
    return netFetch(url, { credentials: 'same-origin', cache: 'no-store' }, 6000).then(function (r) {
      return r && r.ok ? r.text() : null;
    }).then(function (html) {
      if (html == null) return null;
      var m = /data-customer-email="([^"]*)"/.exec(html);
      if (!m) return null;
      // Liquid's `escape` entities, undone by hand (no HTML parsing).
      return m[1].replace(/&#39;/g, '\'').replace(/&quot;/g, '"').replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>').replace(/&#43;/g, '+').replace(/&amp;/g, '&').trim().toLowerCase();
    }).catch(function () { return null; });
  }

  function submitSignedIn() {
    if (state.submitting) return;
    var hpInput = $('[data-slq-hp]');
    state.email = gateLoggedInEmail();
    state.password = '';
    // Not asked, so not sent: the account's marketing choice stays as it is.
    state.marketingConsent = undefined;
    state.honeypot = hpInput ? String(hpInput.value || '') : '';
    // No gate, so no gate token: the first attempt waits for the Turnstile
    // widget on the calculating screen (submitJourney -> whenTurnstileReady).
    state.turnstileToken = null;
    state.signedInSave = true;
    SLQ.track('quiz_signed_in_save', {});
    startSubmit();
  }

  function closeGate() {
    var gate = $('[data-slq-gate]');
    if (gate) gate.setAttribute('hidden', '');
  }

  function gateError(msg) {
    var el = $('[data-slq-gate-error]');
    if (!el) return;
    if (msg) { el.textContent = msg; el.removeAttribute('hidden'); }
    else { el.setAttribute('hidden', ''); }
  }

  // ---------------------------------------- gate: who is typing? (pre-check)
  /* Before the shopper submits, ask the API whether the typed email already
     belongs to a store account ('account'), to a not-yet-activated record or
     a result we already hold ('activate'), or to nobody ('new'), and shape the
     gate to match: the optional password box only makes sense for a brand-new
     shopper (an existing account keeps its password; an existing record is
     activated from the emailed link), and "claim $5" is only true for a
     first-time taker. Coarse, best-effort: any failure reads as 'new', and the
     post-submit routing (staged panel / activation refusal) stays the safety
     net. Never blocks the submit — a fast typer just gets today's behaviour. */
  var GATE_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var GATE_KNOWN_STATES = ['account', 'activate', 'results', 'known'];
  var GATE_LABEL_NEW = 'Email my results + claim $10';
  var GATE_LABEL_KNOWN = 'Email my results';
  var GATE_INTRO_KNOWN = 'We\u2019ll email your skin type and routine so you never lose them.';
  var gateIntroNew = null; // the Liquid copy ($5 + password), captured on first use
  var gateCheckCache = {};
  var gateCheckSeq = 0;
  var gateCheckTimer = null;
  var gateEmailState = 'new';

  function gateLoggedInEmail() {
    return String(root.getAttribute('data-customer-email') || '').trim().toLowerCase();
  }

  function gateLabel() { return gateEmailState === 'new' ? GATE_LABEL_NEW : GATE_LABEL_KNOWN; }

  function checkGateEmail(email) {
    var key = String(email || '').trim().toLowerCase();
    if (!key || !GATE_EMAIL_RE.test(key) || /@temp\.quiz\.internal$/i.test(key)) return Promise.resolve('new');
    if (gateCheckCache[key]) return Promise.resolve(gateCheckCache[key]);
    var base = String(SLQ.settings.ec2Base || '').replace(/\/+$/, '');
    if (!base) return Promise.resolve('new');
    var ctrl = (typeof AbortController === 'function') ? new AbortController() : null;
    var timer = ctrl ? window.setTimeout(function () { try { ctrl.abort(); } catch (e) { /* noop */ } }, 6000) : null;
    return netFetch(base + '/widget/gate/email-check', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: key }),
      signal: ctrl ? ctrl.signal : undefined
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        if (timer) window.clearTimeout(timer);
        var st = (j && GATE_KNOWN_STATES.indexOf(j.state) !== -1) ? j.state : 'new';
        if (j && !j.limited) gateCheckCache[key] = st;
        return st;
      })
      .catch(function () { if (timer) window.clearTimeout(timer); return 'new'; });
  }

  function applyGateEmailState(st, email) {
    var known = GATE_KNOWN_STATES.indexOf(st) !== -1;
    if (st !== gateEmailState && known) SLQ.track('gate_email_known', { state: st });
    gateEmailState = st;
    var pw = $('[data-slq-gate-password]');
    var hint = $('[data-slq-gate-hint]');
    var intro = $('[data-slq-gate-intro]');
    var submit = $('[data-slq-gate-submit]');
    var typed = String(email || '').trim().toLowerCase();
    var me = gateLoggedInEmail();
    if (pw) {
      if (known) { pw.value = ''; pw.setAttribute('hidden', ''); }
      else pw.removeAttribute('hidden');
    }
    // The intro promises $5 and offers a password — both only true for a
    // brand-new shopper. Known email: keep just the "we'll email it" line.
    if (intro) {
      if (gateIntroNew === null) gateIntroNew = intro.innerHTML;
      var wantKnown = known;
      if (wantKnown && intro.getAttribute('data-slq-gate-intro') !== 'known') {
        intro.textContent = GATE_INTRO_KNOWN; intro.setAttribute('data-slq-gate-intro', 'known');
      } else if (!wantKnown && intro.getAttribute('data-slq-gate-intro') === 'known') {
        intro.innerHTML = gateIntroNew; intro.setAttribute('data-slq-gate-intro', '');
      }
    }
    if (hint) {
      hint.textContent = '';
      if (st === 'account' && me && me === typed) {
        hint.textContent = 'You\u2019re signed in \u2014 today\u2019s results will be saved to your account.';
      } else if (st === 'account' && me) {
        // Logged in as someone else: /account/login would just bounce to
        // /account, so no link \u2014 the emailed link (staged flow) is the door.
        hint.textContent = 'This email has its own account, but you\u2019re signed in as ' + me + '. Continue and we\u2019ll email that address a link to apply today\u2019s results \u2014 or sign out and sign in with it first.';
      } else if (st === 'account') {
        /* Save first, then sign in (Luis 10-13): the link submits the gate,
           which saves today's results; the staged panel that follows offers
           Sign in and asks before replacing the saved results. Opening sign-in
           from inside this pop-up left the page frozen behind it. */
        hint.appendChild(document.createTextNode('Welcome back \u2014 this email already has an account with us. '));
        var a = document.createElement('a');
        a.setAttribute('data-slq-gate-login', '');
        a.href = '#';
        a.setAttribute('role', 'button');
        a.textContent = 'Save my results, then sign in';
        a.style.cssText = 'color:#2F3572;font-weight:600;text-decoration:underline';
        hint.appendChild(a);
        hint.appendChild(document.createTextNode(' to replace the results on your account. We\u2019ll also email you a link to apply them later.'));
        /* Signed in elsewhere but not here (live 2026-10-13: the Google app's
           built-in browser on an iPhone does not share Safari's sign-in). */
        hint.appendChild(document.createTextNode(' Signed in on another browser or app? Sign-ins don\u2019t carry over, so this page sees you as signed out.'));
      } else if (st === 'activate') {
        // disabled store record + results on file -> the submit is staged; the
        // panel offers "Activate my account" and a link goes out by email.
        hint.textContent = 'This email already has results on file, but its account hasn\u2019t been activated yet. We\u2019ll email you a link to save today\u2019s results to them.';
      } else if (st === 'results') {
        // results on file, no store record (portal / partner / legacy taker)
        // -> staged; the emailed link is the only door.
        hint.textContent = 'We already have results for this email. We\u2019ll email you a link to save today\u2019s results to them.';
      } else if (st === 'known') {
        // disabled store record, no results (a guest-checkout customer who
        // never took the quiz) -> the submit succeeds as a guest; a password
        // typed here would be refused (the record is not this run's).
        hint.textContent = 'This email is already registered with the store. We\u2019ll email your results \u2014 activate your account any time to sign in and keep them.';
      }
      hint.toggleAttribute('hidden', !known);
    }
    if (submit && !state.submitting) submit.textContent = gateLabel();
  }

  function scheduleGateEmailCheck(immediate) {
    var input = $('[data-slq-gate-email]');
    if (!input) return;
    var email = String(input.value || '').trim().toLowerCase();
    if (gateCheckTimer) { window.clearTimeout(gateCheckTimer); gateCheckTimer = null; }
    if (!email || !GATE_EMAIL_RE.test(email)) { applyGateEmailState('new', email); return; }
    var run = function () {
      gateCheckTimer = null;
      var seq = ++gateCheckSeq;
      checkGateEmail(email).then(function (st) {
        if (seq !== gateCheckSeq) return; // a newer check superseded this one
        var cur = $('[data-slq-gate-email]');
        if (!cur || String(cur.value || '').trim().toLowerCase() !== email) return; // typed on since
        applyGateEmailState(st, email);
      });
    };
    if (immediate || gateCheckCache[email]) run();
    else gateCheckTimer = window.setTimeout(run, 500);
  }

  /* "Log in first": Shopify's return_url brings the shopper back to this very
     page (answers are checkpointed per tab), where the gate re-opens with the
     account email and the results then save straight to the account. Clear
     the legacy checkout_url hint first — snippets/quiz.liquid would otherwise
     bounce a freshly logged-in customer with saved results to the RESULTS
     page instead of back here. */
  function onGateLoginFirst() {
    saveProgress();
    try { localStorage.removeItem('checkout_url'); } catch (e) { /* noop */ }
    SLQ.track('gate_login_first');
  }

  // Turnstile pre-wire (spec §5/D11): blank site key -> render nothing, omit
  // turnstileToken (identical to today). Key set -> render explicit widget.
  function ensureTurnstile() {
    var key = SLQ.settings.turnstileSiteKey;
    if (!key || turnstileWidgetId !== null || turnstileLoading) return;
    turnstileLoading = true;
    var renderWidget = function () {
      // A signed-in save never opens the gate: the widget sits on the
      // calculating screen instead.
      var host = (state.signedInSave && $('[data-slq-turnstile-signed-in]')) || $('[data-slq-turnstile]');
      if (!host || !window.turnstile) return;
      try {
        turnstileWidgetId = window.turnstile.render(host, {
          sitekey: key,
          callback: function (token) { if (turnstilePending) turnstilePending(token); },
          'error-callback': function () { if (turnstilePending) turnstilePending(null); },
          'expired-callback': function () { if (turnstilePending) turnstilePending(null); }
        });
      } catch (e) { /* Turnstile render failed: submit proceeds token-less; server decides */ }
    };
    if (window.turnstile && typeof window.turnstile.render === 'function') {
      renderWidget();
      return;
    }
    window.__slqTurnstileReady = renderWidget;
    var s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__slqTurnstileReady&render=explicit';
    s.async = true;
    s.defer = true;
    document.head.appendChild(s);
  }

  function readTurnstileToken() {
    if (turnstileWidgetId === null || !window.turnstile) return null;
    try {
      var t = window.turnstile.getResponse(turnstileWidgetId);
      return t || null;
    } catch (e) { return null; }
  }

  /* Cloudflare Turnstile tokens are SINGLE-USE and short-lived. The submit
     flow can retry (network/5xx x2, plus the 409 in-flight poll up to ~2 min),
     so reusing the gate's token would fail verification with
     "timeout-or-duplicate" the moment Turnstile keys are provisioned. Each
     retry therefore resets the widget and waits for a brand-new token.
     Resolves null (never throws, never blocks the funnel) when Turnstile is
     absent, errors, or does not answer within the timeout — the server then
     applies its own policy. */
  var TURNSTILE_REFRESH_TIMEOUT_MS = 8000;

  /* Wait for the Turnstile script + widget to actually exist. A visitor can
     submit the gate within a few hundred ms of it opening — long before
     Cloudflare's script has loaded — and without this the request would carry
     no token at all (invisible today because verification fails open, but a
     403 for real people the moment keys are provisioned). */
  function whenTurnstileReady(timeoutMs) {
    return new Promise(function (resolve) {
      if (!SLQ.settings || !SLQ.settings.turnstileSiteKey) { resolve(false); return; }
      if (turnstileWidgetId !== null) { resolve(true); return; }
      ensureTurnstile();
      var waited = 0;
      var tick = window.setInterval(function () {
        if (turnstileWidgetId !== null) { window.clearInterval(tick); resolve(true); return; }
        waited += 100;
        if (waited >= timeoutMs) { window.clearInterval(tick); resolve(false); }
      }, 100);
    });
  }

  function refreshTurnstileToken() {
    return new Promise(function (resolve) {
      if (turnstileWidgetId === null || !window.turnstile) { resolve(null); return; }
      var settled = false;
      var finish = function (token) {
        if (settled) return;
        settled = true;
        turnstilePending = null;
        resolve(token || null);
      };
      turnstilePending = finish;
      window.setTimeout(function () { finish(readTurnstileToken()); }, TURNSTILE_REFRESH_TIMEOUT_MS);
      try {
        window.turnstile.reset(turnstileWidgetId);
        // Explicit-execution widgets need an execute() kick; for auto widgets
        // reset() alone re-runs the challenge and fires the callback.
        if (typeof window.turnstile.execute === 'function') {
          try { window.turnstile.execute(turnstileWidgetId); } catch (e) { /* auto mode */ }
        }
      } catch (e) { finish(null); }
    });
  }

  /* The outcome panel's "Try again".
   *
   * state.turnstileToken holds the token the gate spent on the attempt that
   * just failed, and Turnstile tokens are SINGLE-USE: re-sending one verifies
   * as "timeout-or-duplicate", so the server answers 403 "Bot verification
   * failed" regardless of what caused the ORIGINAL failure. Observed live
   * 2026-08-10 — a 500 retried straight into a 403, so the button could never
   * recover anything. submitWithRetries already re-mints between its own
   * attempts; this outcome-panel retry was the one path that skipped it.
   *
   * Failing to obtain a fresh token does NOT block the retry: we submit with a
   * null token and let the server apply its own policy. That is the same
   * posture the gate takes when the Turnstile script is blocked or absent, so
   * a visitor behind a strict blocker is never trapped by their own retry. */
  function retryWithFreshToken() {
    if (state.submitting) return;
    var configured = !!(SLQ.settings && SLQ.settings.turnstileSiteKey);
    if (!configured) { startSubmit(); return; }

    var btn = $('[data-slq-submit-retry]');
    var restoreLabel = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Verifying…'; }

    // NB: not named `go` — that is the module-level view switcher.
    var resubmit = function (tok) {
      state.turnstileToken = tok || null;
      if (btn) { btn.disabled = false; btn.textContent = restoreLabel; }
      startSubmit();
    };

    whenTurnstileReady(TURNSTILE_REFRESH_TIMEOUT_MS).then(function (ready) {
      if (!ready) { resubmit(null); return; }
      refreshTurnstileToken().then(resubmit);
    });
  }

  /* Both gate exits — "Email my results" and "Skip" — run through here.
     `skip` leaves state.email empty; the orchestrator then generates a
     guest_*@temp.quiz.internal address (app/api/quiz/submit/route.ts), so
     results are never paywalled behind an email.

     The captcha is enforced on BOTH paths and is deliberately stricter on
     skip: with an email we can rate-limit and de-duplicate by submission
     fingerprint, but a skipped submission has no such handle, so Turnstile is
     the only thing between a bot and the scoring Lambda. When Turnstile is
     configured but has not produced a token yet we wait for it rather than
     letting the request through — the whole point of the skip path is that a
     human passes it silently and a bot cannot. */
  function proceedFromGate(skip) {
    if (state.submitting) return;
    var emailInput = $('[data-slq-gate-email]');
    var consentInput = $('[data-slq-gate-consent]');
    var passwordInput = $('[data-slq-gate-password]');
    var hpInput = $('[data-slq-hp]');
    var email = emailInput ? String(emailInput.value || '').trim() : '';
    // Optional: a password turns the store record this quiz creates into a
    // real account (see finishSuccess -> activateGateAccount). Never required.
    var password = (!skip && passwordInput && !passwordInput.hasAttribute('hidden')) ? String(passwordInput.value || '') : '';

    if (!skip) {
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        gateError('Enter a valid email, or choose “Skip” to see your results without one.');
        return;
      }
      if (password && (password.length < 8 || password.length > 100 || /^\s|\s$/.test(password))) {
        gateError('Passwords need at least 8 characters (no spaces at the start or end) — or leave it blank to skip the account.');
        return;
      }
    }

    var turnstileConfigured = !!(SLQ.settings && SLQ.settings.turnstileSiteKey);
    var proceed = function (token) {
      if (turnstileConfigured && !token) {
        gateError('We couldn’t verify you’re human. Please wait a moment and try again.');
        setGateBusy(false);
        return;
      }
      gateError(null);
      state.signedInSave = false;
      state.email = skip ? '' : email;
      state.password = skip ? '' : password;
      state.marketingConsent = skip ? false : !!(consentInput && consentInput.checked);
      state.honeypot = hpInput ? String(hpInput.value || '') : '';
      state.turnstileToken = token;
      setGateBusy(false);
      closeGate();
      startSubmit();
    };

    if (!turnstileConfigured) { proceed(null); return; }
    var existing = readTurnstileToken();
    if (existing) { proceed(existing); return; }
    // Widget still solving (or not yet mounted): wait, don't wave it through.
    setGateBusy(true);
    whenTurnstileReady(TURNSTILE_REFRESH_TIMEOUT_MS).then(function (ready) {
      if (!ready) { proceed(null); return; } // script blocked/absent — server decides
      var tok = readTurnstileToken();
      if (tok) { proceed(tok); return; }
      refreshTurnstileToken().then(proceed);
    });
  }

  function setGateBusy(busy) {
    var submit = $('[data-slq-gate-submit]');
    var skip = $('[data-slq-gate-skip]');
    [submit, skip].forEach(function (b) {
      if (!b) return;
      b.disabled = !!busy;
      b.style.opacity = busy ? '.6' : '';
    });
    if (submit) submit.textContent = busy ? 'Verifying…' : gateLabel();
  }

  function onGateSubmit(ev) {
    ev.preventDefault();
    proceedFromGate(false);
  }

  // ------------------------------------------------------------- calculating

  function resetCalcUI() {
    var anim = $('[data-slq-calc-anim]');
    if (anim) anim.removeAttribute('hidden');
    ['staged', 'login', 'failed'].forEach(function (k) {
      var el = $('[data-slq-outcome-' + k + ']');
      if (el) el.setAttribute('hidden', '');
    });
    $all('[data-slq-calc-row]').forEach(function (row) { setCalcRow(row, 'pending'); });
    var bar = $('[data-slq-calc-bar]');
    if (bar) bar.style.width = '0%';
    state.calcStage = 0;
  }

  function setCalcRow(row, rowState) {
    row.setAttribute('data-state', rowState);
    var done = row.querySelector('[data-slq-calc-done]');
    var active = row.querySelector('[data-slq-calc-active]');
    var pending = row.querySelector('[data-slq-calc-pending]');
    if (done) done.toggleAttribute('hidden', rowState !== 'done');
    if (active) active.toggleAttribute('hidden', rowState !== 'active');
    if (pending) pending.toggleAttribute('hidden', rowState !== 'pending');
  }

  // Prototype startCalc() cadence (950ms per stage); the LAST row stays active
  // (spinner) until the real submit round-trip resolves — no fabricated finish.
  function startCalcAnimation() {
    clearCalcTimers();
    resetCalcUI();
    var rows = $all('[data-slq-calc-row]');
    var bar = $('[data-slq-calc-bar]');
    if (rows[0]) setCalcRow(rows[0], 'active');
    if (bar) bar.style.width = '12%';
    var advanceStage = function (stage) {
      // stage i: rows[0..i-1] done, rows[i] active; never auto-complete the last row
      for (var i = 0; i < rows.length; i++) {
        if (i < stage) setCalcRow(rows[i], 'done');
        else if (i === stage) setCalcRow(rows[i], 'active');
        else setCalcRow(rows[i], 'pending');
      }
      if (bar) bar.style.width = Math.round(((stage) / rows.length) * 90 + 10) + '%';
    };
    for (var stage = 1; stage < rows.length; stage++) {
      (function (s) {
        state.calcTimers.push(window.setTimeout(function () { advanceStage(s); }, CALC_STAGE_MS * s));
      })(stage);
    }
  }

  function finishCalcAnimation() {
    clearCalcTimers();
    $all('[data-slq-calc-row]').forEach(function (row) { setCalcRow(row, 'done'); });
    var bar = $('[data-slq-calc-bar]');
    if (bar) bar.style.width = '100%';
  }

  function clearCalcTimers() {
    state.calcTimers.forEach(function (t) { window.clearTimeout(t); });
    state.calcTimers = [];
  }

  // ------------------------------------------------------------------- submit

  function submitUrl() {
    if (SLQ.settings.useProxySubmit) {
      return stripTrailingSlash(SLQ.settings.proxyBase) + '/submit';
    }
    return stripTrailingSlash(SLQ.settings.nextBase) + '/api/quiz/submit';
  }

  /* Per-attempt nonce (spec D22 / §13.2 step 1).
   *
   * Shopify's App Proxy signature covers a per-SECOND timestamp and the query
   * params — never the POST body. Two attempts inside the same second are
   * therefore byte-identical, and the façade's single-use replay cache cannot
   * tell a legitimate retry from an attacker's replay: it 401s the retry, and
   * 401 is in DETERMINISTIC_NO_RETRY_STATUSES, so the shopper's quiz dies for
   * good. Appending one fresh UUID per attempt makes every attempt's signature
   * unique, which is what lets the replay cache be strict without eating real
   * retries.
   *
   * This is the ONLY parameter the client may ever append (spec SEC-11): a
   * random UUID is neither PII nor a credential. submissionId, session_uuid,
   * answers and email all stay byte-identical across retries so the ledger's
   * idempotency fingerprint is unaffected. */
  function withNonce(url) {
    var nonce;
    try {
      nonce = (window.crypto && typeof window.crypto.randomUUID === 'function')
        ? window.crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = (Math.random() * 16) | 0;
            return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
          });
    } catch (e) { return url; }
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'nonce=' + nonce;
  }

  function attemptSubmit(url, bodyString) {
    return netFetch(withNonce(url), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: bodyString
    }, SUBMIT_TIMEOUT_MS).then(function (response) {
      return response.json().catch(function () { return null; }).then(function (data) {
        if (response.status === 409) {
          if (data && data.code === 'SUBMISSION_IN_FLIGHT') {
            var retryAfter = (data && typeof data.retryAfter === 'number') ? data.retryAfter : 5;
            return { kind: 'in-flight', retryAfter: retryAfter };
          }
          if (data && data.code === 'LOGIN_REQUIRED') {
            return { kind: 'login-required', email: data.email, message: data.message };
          }
          return { kind: 'server-error', status: response.status };
        }
        if (!response.ok) return { kind: 'server-error', status: response.status };
        if (data && (data.requireLoginToSave || (data.staged && data.stagingNonce))) return { kind: 'staged', body: data };
        if (data && data.replayed && data.recovered) return { kind: 'recovered', body: data };
        return { kind: 'success', body: data };
      });
    }).catch(function () {
      return { kind: 'network-error' };
    });
  }

  // One attempt-set = one submissionId, and the ledger's idempotency
  // fingerprint is sha256(email | answers) — so answers, email and
  // submissionId stay byte-identical across every internal retry (spec §5).
  // The ONLY field re-issued per attempt is turnstileToken, which must be
  // fresh because Turnstile tokens are single-use; it is outside the
  // fingerprint, so idempotency is unaffected.
  // Budgets: 409-in-flight poll x24 @5s; network/5xx x2 @1s; 400/401/403 never.
  /* The gate's pre-check said this email already has results/an account and
     the shopper is not signed in as it: the server will STAGE the submit, so
     a plain "saved" answer would be wrong. */
  function stagingExpected(email) {
    if (['account', 'activate', 'results'].indexOf(gateEmailState) === -1) return false;
    var typed = String(email || '').trim().toLowerCase();
    var me = gateLoggedInEmail();
    return !(me && typed && me === typed);
  }

  function submitJourney(bodyObject) {
    var url = submitUrl();
    var networkRetries = 0;
    var inFlightAttempts = 0;
    var recoveredRechecks = 0;
    var attempted = false;
    var submittedEmail = (bodyObject.userInfo && bodyObject.userInfo.email) || bodyObject.email || state.email;

    function bodyForAttempt() {
      var configured = !!(SLQ.settings && SLQ.settings.turnstileSiteKey);
      if (!configured) { attempted = true; return Promise.resolve(JSON.stringify(bodyObject)); }

      var isFirst = !attempted;
      attempted = true;
      // First attempt: keep the gate's token if it already exists, otherwise
      // wait for the widget to finish loading and issue one. Retries: always a
      // brand-new token, since Turnstile tokens are single-use.
      if (isFirst && bodyObject.turnstileToken) return Promise.resolve(JSON.stringify(bodyObject));

      return whenTurnstileReady(TURNSTILE_REFRESH_TIMEOUT_MS).then(function (ready) {
        if (!ready) { delete bodyObject.turnstileToken; return JSON.stringify(bodyObject); }
        var existing = isFirst ? readTurnstileToken() : null;
        if (existing) { bodyObject.turnstileToken = existing; return JSON.stringify(bodyObject); }
        return refreshTurnstileToken().then(function (token) {
          if (token) bodyObject.turnstileToken = token;
          else delete bodyObject.turnstileToken;
          return JSON.stringify(bodyObject);
        });
      });
    }

    function loop() {
      return bodyForAttempt().then(function (bodyString) {
        return attemptSubmit(url, bodyString);
      }).then(function (result) {
        if (result.kind === 'network-error' || result.kind === 'server-error') {
          var deterministic = result.kind === 'server-error' &&
            DETERMINISTIC_NO_RETRY_STATUSES.indexOf(result.status) !== -1;
          if (deterministic || networkRetries >= NETWORK_MAX_RETRIES) {
            return {
              kind: 'failed',
              code: result.kind === 'network-error' ? 'NETWORK_ERROR' : ('SERVER_ERROR_' + result.status)
            };
          }
          networkRetries++;
          return delay(NETWORK_RETRY_DELAY_MS).then(loop);
        }
        if (result.kind === 'in-flight') {
          inFlightAttempts++;
          if (inFlightAttempts >= IN_FLIGHT_MAX_ATTEMPTS) {
            return { kind: 'failed', code: 'SUBMISSION_IN_FLIGHT_EXHAUSTED' };
          }
          return delay(result.retryAfter * 1000).then(loop);
        }
        if (result.kind === 'recovered' && stagingExpected(submittedEmail)) {
          if (recoveredRechecks < RECOVERED_RECHECK_MAX) {
            recoveredRechecks++;
            return delay(RECOVERED_RECHECK_DELAY_MS).then(loop);
          }
          /* Still no staging envelope: do NOT show the results as saved.
             The account's sign-in is the honest next step. */
          SLQ.track('quiz_submit_recovered_unstaged', {});
          return { kind: 'login-required', email: submittedEmail };
        }
        return result; // login-required | staged | recovered | success
      });
    }
    return loop();
  }

  function startSubmit() {
    if (state.submitting) return;

    // Last line of defence: never POST a partial answer set. Scoring a partial
    // set returns 500 and the outcome panel's "Try again" just re-sends it, so
    // the shopper is stuck. Send them to the gap instead — their answers and
    // their place in the quiz are preserved.
    var gap = firstIncompleteQuestionIndex();
    if (gap !== -1) {
      SLQ.track('quiz_submit_blocked_incomplete', { questionId: state.questions[gap].id });
      state.currentIndex = gap;
      go('quiz');
      renderQuestion();
      showError('Please answer this question so we can score your quiz.');
      shake();
      return;
    }

    state.submitting = true;

    var submissionId = uuid();
    var body = buildSubmitRequestBody({
      answers: state.answers,
      questions: state.questions,
      email: state.email,
      marketingConsent: state.marketingConsent,
      affiliateCode: state.affiliateCode,
      turnstileToken: state.turnstileToken,
      honeypot: state.honeypot,
      submissionId: submissionId,
      sessionUuid: sessionUuid(),
      configVersion: state.configVersionInt
    });
    // v6.2 §4: only after Start on the "will see your result" notice.
    if (invite && invite.share === true && !invite.demo) body.friendInvite = { inviteId: invite.id, shareConsent: true };
    // v6.3 §6.2: the Refer a friend invite this visitor came in on (assets/sts-ref-track.js; null while its setting is off).
    var refT = window.STS && window.STS.refTrack, refId = refT && refT.ref && refT.ref('quiz submit');
    if (refId) body.referral = { ref: refId };

    go('calculating');
    startCalcAnimation();
    state.calcStartedAt = Date.now();

    submitJourney(body).then(function (outcome) {
      // calculating min display (2.5s) so it never flashes
      var elapsed = Date.now() - state.calcStartedAt;
      var wait = Math.max(0, CALC_MIN_DISPLAY_MS - elapsed);
      return delay(wait).then(function () { return outcome; });
    }).then(function (outcome) {
      state.submitting = false;
      applyOutcome(outcome);
    });
  }

  function applyOutcome(outcome) {
    if (outcome.kind === 'success' || outcome.kind === 'recovered') {
      clearProgress();
      finishCalcAnimation();
      window.setTimeout(function () { finishSuccess(outcome.body || {}); }, 400);
      return;
    }
    clearCalcTimers();
    var anim = $('[data-slq-calc-anim]');
    if (anim) anim.setAttribute('hidden', '');
    if (outcome.kind === 'staged') {
      /* The legacy quiz's contract for a returning taker who is logged OUT:
         a Shopify account exists -> log in (or activate) on THIS store and the
         new result is applied to it. The server told us which case this is
         (stagedShopifyAccount, from a Shopify lookup by email). Enabled ->
         /account/login; disabled|invited -> the activation page; no store
         account -> the emailed link is the only door. Either way the results
         page ASKS before anything is overwritten. The confirmation email is
         sent in every case, so "later" always works too. */
      var body = outcome.body || {};
      var acct = body.stagedShopifyAccount;
      var acctState = acct && acct.exists ? String(acct.state || '').toLowerCase() : '';
      var resultsPath = String(SLQ.settings.resultsUrl || '/pages/skin-type-results').split('?')[0];
      var backTo = resultsPath + '?staged=' + encodeURIComponent(body.stagingNonce || '');
      var stagedTitle = $('[data-slq-outcome-staged-title]');
      var stagedMsg = $('[data-slq-outcome-staged-msg]');
      var stagedLogin = $('[data-slq-outcome-staged-login]');
      var email = body.stagedEmail ? String(body.stagedEmail) : 'this email';
      var meNow = gateLoggedInEmail();
      var stagedLower = body.stagedEmail ? String(body.stagedEmail).trim().toLowerCase() : '';
      var stagedForgot = $('[data-slq-outcome-staged-forgot]');
      if (stagedForgot) stagedForgot.setAttribute('hidden', '');
      if (meNow && stagedLower && meNow === stagedLower) {
        /* The page was signed in to THIS account, yet the store no longer saw
           the sign-in when the answers arrived: the session ended while the
           quiz was open (or the sign-in was on another site). Say so plainly
           instead of a bare "sign in", and offer the password reset, since
           the shopper believed they were already signed in. */
        SLQ.track('quiz_session_expired_staged', { account: acctState || 'none' });
        if (stagedTitle) stagedTitle.textContent = 'Your results are ready — your sign-in timed out';
        if (stagedMsg) stagedMsg.textContent = 'You were signed in when you started, but the store signed you out before you finished, so we couldn’t save these results to your account yet. Nothing is lost: sign in again and they’ll be applied. We’ve also emailed ' + email + ' a link to apply them later.';
        if (stagedLogin) {
          stagedLogin.textContent = 'Sign in again';
          stagedLogin.setAttribute('href', '/account/login?return_url=' + encodeURIComponent(backTo));
          stagedLogin.setAttribute('data-slq-claim-login', backTo);
          stagedLogin.removeAttribute('hidden');
        }
        if (stagedForgot) stagedForgot.removeAttribute('hidden');
      } else if (acctState === 'enabled' && meNow && stagedLower && meNow !== stagedLower) {
        /* Logged in as a DIFFERENT account than the email on the quiz. The
           usual "Log in" would just bounce (/account/login -> /account) — offer
           the log-out instead; checkout_url (set below) brings the next login,
           as the right account, straight back to the claim prompt. */
        if (stagedTitle) stagedTitle.textContent = 'Your new results are ready \u2014 but you\u2019re signed in as another account';
        if (stagedMsg) stagedMsg.textContent = 'These results belong to ' + email + ', and you\u2019re signed in as ' + meNow + '. Sign out, then sign in with ' + email + ' and we\u2019ll ask you to confirm before anything changes. We\u2019ve also emailed ' + email + ' a link, in case you\u2019d rather do it later.';
        if (stagedLogin) {
          stagedLogin.textContent = 'Sign out to switch accounts';
          stagedLogin.setAttribute('href', '/account/logout');
          stagedLogin.setAttribute('data-slq-claim-login', backTo);
          stagedLogin.removeAttribute('hidden');
        }
      } else if (acctState === 'enabled') {
        if (stagedTitle) stagedTitle.textContent = 'Your new results are ready \u2014 sign in to apply them';
        if (stagedMsg) stagedMsg.textContent = 'This email already has results saved on your account. Sign in and we\u2019ll ask you to confirm before anything changes. We\u2019ve also emailed ' + email + ' a link, in case you\u2019d rather do it later.';
        if (stagedLogin) {
          stagedLogin.textContent = 'Sign in';
          stagedLogin.setAttribute('href', '/account/login?return_url=' + encodeURIComponent(backTo));
          stagedLogin.setAttribute('data-slq-claim-login', backTo);
          stagedLogin.removeAttribute('hidden');
        }
        /* A shopper in a phone app's built-in browser (the Google app,
           Instagram) is signed out there even when signed in on Safari, and may
           never have set a password (Sign in with Shop): offer the reset. */
        if (stagedForgot) stagedForgot.removeAttribute('hidden');
      } else if (acctState === 'disabled' || acctState === 'invited') {
        if (stagedTitle) stagedTitle.textContent = 'Your new results are ready \u2014 activate your account to apply them';
        if (stagedMsg) stagedMsg.textContent = 'This email already has results on file, but the account has not been activated yet. Activate it and we\u2019ll ask you to confirm before anything changes. We\u2019ve also emailed ' + email + ' a link, in case you\u2019d rather do it later.';
        if (stagedLogin) {
          stagedLogin.textContent = 'Activate my account';
          stagedLogin.setAttribute('href', '/pages/customer-activate');
          stagedLogin.setAttribute('data-slq-claim-login', backTo);
          stagedLogin.removeAttribute('hidden');
        }
      } else {
        if (stagedTitle) stagedTitle.textContent = 'Check your email to apply your new results';
        if (stagedMsg) stagedMsg.textContent = 'This email already has results on file, so we\u2019ve sent a confirmation link to ' + email + '. Open that email and select \u201CApply my new results\u201D to save today\u2019s quiz.';
        if (stagedLogin) stagedLogin.setAttribute('hidden', '');
      }
      // A password typed at the gate is only ever applied to the record THIS
      // submission created. This email already had results, so say so plainly
      // instead of leaving the shopper to wonder what happened to it.
      if (state.password && stagedMsg) {
        stagedMsg.textContent += ' The password you just entered was not used \u2014 this email already has an account with the store.';
        state.password = '';
      }
      // Remember where to come back to after login/activation. quiz.liquid
      // (legacy, still in the layout) redirects a freshly logged-in customer
      // to localStorage.checkout_url — the same hand-off the legacy quiz used.
      if (acctState) {
        try { localStorage.setItem('checkout_url', backTo); } catch (e) { /* storage blocked */ }
      }
      var staged = $('[data-slq-outcome-staged]');
      if (staged) staged.removeAttribute('hidden');
      return;
    }
    if (outcome.kind === 'login-required') {
      var loginMsg = $('[data-slq-outcome-login-msg]');
      if (loginMsg) {
        loginMsg.textContent = state.signedInSave
          // started signed in, but the store no longer saw the sign-in (expired session)
          ? 'Your sign-in timed out before you finished, so we couldn’t save these results to your account. Sign in again and your answers will be waiting here.'
          : (outcome.message ||
            ('This email' + (outcome.email ? (' (' + outcome.email + ')') : '') + ' belongs to an existing account. Please sign in to save your results.'));
      }
      var login = $('[data-slq-outcome-login]');
      if (login) login.removeAttribute('hidden');
      return;
    }
    // failed
    var failedMsg = $('[data-slq-outcome-failed-msg]');
    if (failedMsg) {
      failedMsg.textContent = 'We couldn’t score your quiz. Your answers are still here — please try again.';
    }
    var failed = $('[data-slq-outcome-failed]');
    if (failed) failed.removeAttribute('hidden');
  }

  // ------------------------------------------------------------------ success

  /* POST /widget/account/activate with the freshly minted widget token. Resolves
     to the Multipass login URL on success, or null on ANY refusal/failure
     (409 = the email already has an account or an older unactivated record —
     the emailed link / "Log in" remain the door; 503 = box not configured).
     The password is sent once over TLS and dropped from state immediately. */
  function activateGateAccount(password, resultsUrl) {
    var base = String(SLQ.settings.ec2Base || '').replace(/\/+$/, '');
    if (!base || !SLQ.state || !SLQ.state.widgetToken) return Promise.resolve(null);
    var returnTo = '/pages/skin-type-results';
    try {
      var u = new URL(String(resultsUrl || returnTo), window.location.origin);
      if (u.origin === window.location.origin) returnTo = u.pathname + u.search;
    } catch (e) { /* keep the default */ }
    var ctrl = (typeof AbortController === 'function') ? new AbortController() : null;
    var timer = ctrl ? window.setTimeout(function () { try { ctrl.abort(); } catch (e2) { /* noop */ } }, 12000) : null;
    return netFetch(base + '/widget/account/activate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + SLQ.state.widgetToken },
      body: JSON.stringify({ password: password, returnTo: returnTo, marketingConsent: !!state.marketingConsent }),
      signal: ctrl ? ctrl.signal : undefined
    })
      .then(function (r) { return r.json().catch(function () { return {}; }).then(function (j) { return { ok: r.ok, status: r.status, body: j }; }); })
      .then(function (res) {
        if (timer) window.clearTimeout(timer);
        if (res.ok && res.body && res.body.ok && typeof res.body.loginUrl === 'string' && /^https:\/\//.test(res.body.loginUrl)) {
          SLQ.track('gate_account_created');
          return res.body.loginUrl;
        }
        SLQ.track('gate_account_refused', { status: res.status, error: res.body && res.body.error });
        return null;
      })
      .catch(function (err) {
        if (timer) window.clearTimeout(timer);
        try { console.warn('[SLQ] account activation failed:', err && err.message); } catch (e3) { /* noop */ }
        SLQ.track('gate_account_failed');
        return null;
      });
  }

  function finishSuccess(body) {
    var skinType = body.skinType || null;
    var sensitivityLevel = ('sensitivityLevel' in body) ? body.sensitivityLevel : null;

    SLQ.state = {
      widgetToken: body.widgetToken || null,
      skinType: skinType,
      sensitivityLevel: sensitivityLevel,
      customerId: ('customerId' in body) ? body.customerId : null,
      regimenId: ('regimenId' in body) ? body.regimenId : null,
      quizId: body.quizId || null,
      submissionId: body.submissionId || null,
      email: state.email
    };
    ssSet('slq_result', JSON.stringify({
      widgetToken: SLQ.state.widgetToken,
      skinType: SLQ.state.skinType,
      sensitivityLevel: SLQ.state.sensitivityLevel
    }));

    // localStorage ALLOW-LIST ONLY (spec §3). The legacy re-arm flags and the
    // legacy routine cache key from the spec's forbid list are NEVER written.
    if (skinType) lsSet('customerSkinType', skinType);
    if (Array.isArray(body.sensitivityList)) {
      lsSet('customerSensitivityList', JSON.stringify(body.sensitivityList));
    } else if (sensitivityLevel) {
      // outcome carries a single real sensitivity code; store it in the
      // legacy JSON-array shape without inventing additional values
      lsSet('customerSensitivityList', JSON.stringify([sensitivityLevel]));
    }
    if (state.email) { lsSet('customerEmail', state.email); lsSet('customerEmailAt', String(Date.now())); }
    // Klaviyo onsite identity for the email just given (assets/sts-track.js G13c).
    try { if (state.email && window.STSTrack && window.STSTrack.identifyEmail) window.STSTrack.identifyEmail(state.email); } catch (e) { /* never block the results */ }
    /* Which gate exit THIS submission took, for the results page's inline
       "$10 off ... for entering your email" line (2026-09-25; it was a toast
       until v5.1 handoff 1.5): set on the email path, cleared on Skip. A tab
       flag, never the email itself. */
    try {
      if (state.email) sessionStorage.setItem('slqGateEmailed', '1');
      else sessionStorage.removeItem('slqGateEmailed');
    } catch (e) { /* storage blocked: no line, which is the safe side */ }
    if (state.affiliateCode) lsSet('affiliateCode', state.affiliateCode);
    // The invite went with this submit (or was declined): a retake is their own.
    if (invite) { invite = null; try { window.sessionStorage.removeItem(INVITE_KEY); } catch (e) { /* private mode */ } }

    // per-type theming from the shared ramp; the shop metafield's colour only
    // when the ramp JSON is absent (never guessed)
    if (skinType) setTypeColors(skinType);

    SLQ.track('quiz_complete');
    // beacon: the results redirect follows immediately and must not lose it
    gaEvent('completed', { skin_type: skinType, sensitivity: sensitivityLevel, transport_type: 'beacon' });

    /* Separate results page (settings.results_url). The routine gets its own
       URL so it can be returned to later. The handoff rides sessionStorage —
       NOT the query string: widgetToken is a bearer credential and a URL leaks
       through Referer, history, and any analytics that records paths.

       A visitor who gave us an email ("saved" their results) also gets a
       namespaced localStorage copy, so coming back later lands them straight
       on their routine without logging in. Namespaced under slq* on purpose:
       `customerRoutine` and the re-arm flags are on the spec's forbid list. */
    var resultsUrl = SLQ.settings && SLQ.settings.resultsUrl;
    if (resultsUrl) {
      /* NO TIMESTAMP HERE, and that is a decision, not an oversight — it has
         been rediscovered twice (PR #216, then the audit of every reader after
         it), so it is written down. A `savedAt` would make "is this stale?"
         askable, which is the structural fix; slqSavedResults has one and this
         does not, so no reader can order the pair on freshness and they all
         order on AUTHORITY instead. It is not written because a timestamp only
         some readers honour is worse than one consistent ordering, and adding
         it changes behaviour on contact: assets/sts-fbt.js guestToken() already
         applies a two-hour TTL to `s.savedAt` on BOTH sources and would start
         rejecting handoffs, while assets/sts-steps.js reasons from its absence
         and would not. Add it only with a pass over every reader — sts-steps,
         sts-fbt, regimens, sts-auth — deciding what "older than the token's two
         hours" means on each. Until then, a reader needing to tell the blobs
         apart uses `customerSkinType`, rewritten by every completion above. */
      var handoff = {
        widgetToken: SLQ.state.widgetToken,
        skinType: SLQ.state.skinType,
        sensitivityLevel: SLQ.state.sensitivityLevel
      };
      try { sessionStorage.setItem('slqResultsHandoff', JSON.stringify(handoff)); } catch (e) { /* noop */ }
      if (state.email) {
        try {
          localStorage.setItem('slqSavedResults', JSON.stringify({
            widgetToken: handoff.widgetToken,
            skinType: handoff.skinType,
            sensitivityLevel: handoff.sensitivityLevel,
            savedAt: Date.now()
          }));
        } catch (e) { /* noop */ }
      }
      /* The gate's optional password: turn the store record this submission
         just created into a real account, then arrive on the results page
         LOGGED IN (Multipass; the handoff above rides sessionStorage through
         the hop, so the page still renders from the token). Any refusal or
         failure simply continues to the results as a guest — an account is a
         bonus on top of the results, never a condition for seeing them. */
      if (state.password && SLQ.state.widgetToken) {
        var pw = state.password;
        state.password = '';
        activateGateAccount(pw, resultsUrl).then(function (loginUrl) {
          /* snippets/quiz.liquid (legacy, still in the layout) stores the
             logged-out page path as checkout_url and, on the first logged-in
             page, redirects to it — for a quiz path that means the BARE
             results URL, which on the dev template drops ?view= and lands on
             the wrong results page. The Multipass return_to already carries
             the right destination, so clear the legacy hint before the hop. */
          if (loginUrl) { try { localStorage.removeItem('checkout_url'); } catch (e2) { /* noop */ } }
          window.location.href = loginUrl || resultsUrl;
        });
        return;
      }
      window.location.href = resultsUrl;
      return;
    }

    go('results');
    if (SLQ.results && typeof SLQ.results.show === 'function') {
      SLQ.results.show(SLQ.state);
    } else {
      // results file not initialized yet — it must consume SLQ.pendingResult on load
      SLQ.pendingResult = SLQ.state;
    }
  }

  // ------------------------------------------------------------------ landing

  function wireLanding() {
    $all('[data-slq-start]').forEach(function (btn) {
      btn.addEventListener('click', startQuiz);
    });

    var exitBtn = $('[data-slq-exit]');
    if (exitBtn) exitBtn.addEventListener('click', function () { go('landing'); });

    var copyBtn = $('[data-slq-hero-copy-btn]');
    var copyMobile = $('[data-slq-hero-copy-mobile]');
    var copyArrow = $('[data-slq-hero-copy-arrow]');
    if (copyBtn && copyMobile) {
      copyBtn.addEventListener('click', function () {
        var open = copyMobile.hasAttribute('hidden');
        copyMobile.toggleAttribute('hidden', !open);
        copyBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (copyArrow) copyArrow.textContent = open ? '▴' : '▾';
      });
    }

    // Dr. Baumann's full intro. She prefers the long version; it is too long
    // for the hero, so it lives behind a Learn more toggle like the
    // per-question info button.
    var moreBtn = $('[data-slq-hero-more-btn]');
    var morePanel = $('[data-slq-hero-more]');
    var moreArrow = $('[data-slq-hero-more-arrow]');
    if (moreBtn && morePanel) {
      moreBtn.addEventListener('click', function () {
        var open = moreBtn.getAttribute('aria-expanded') === 'true';
        moreBtn.setAttribute('aria-expanded', open ? 'false' : 'true');
        morePanel.toggleAttribute('hidden', open);
        if (moreArrow) moreArrow.textContent = open ? '▾' : '▴';
      });
    }

    // Explore-16 octagon grid: each tile's octagon paints itself from the
    // tokens (data-sts-type -> --sts-type-oct, sections/sts-liquid-quiz.liquid),
    // so nothing is filled here; caption derives trait words from the code
    // letters (AXES)
    var capCode = $('[data-slq-grid-cap-code]');
    var capText = $('[data-slq-grid-cap-text]');
    $all('[data-slq-type-tile]').forEach(function (tile) {
      var code = tile.getAttribute('data-slq-type-code');
      tile.addEventListener('click', function () {
        $all('[data-slq-type-tile]').forEach(function (t) { t.parentElement.style.borderColor = 'transparent'; });
        tile.parentElement.style.borderColor = '#1b1d3a';
        if (capCode) capCode.textContent = code;
        if (capText) {
          capText.textContent = code.split('').map(function (l) { return AXES[l] || l; }).join(' · ');
        }
      });
    });
  }

  // --------------------------------------------------------------------- init

  // Content review: Dr. Baumann reviews question copy on dev before it ships to
  // production, but the quiz page is hardwired to the production API. Rather
  // than repoint the whole theme (a published theme pointing at dev would send
  // real patients to the dev database), ?slqEnv= swaps the API base for that
  // one visit only. Allowlisted on purpose — never take a host from the URL.
  var ENV_BASES = {
    dev: 'https://dev.skintype.pro',
    staging: 'https://staging.skintype.pro',
    prod: 'https://app.skintype.pro'
  };

  /* SEC-04: honoured only off the published theme, fail closed: the server's
     theme.role must be known and not 'main' (the sts-env api_env gate). */
  function slqEnvAllowed() {
    var r = String((window.__STS_SERVER__ || {}).themeRole || ''), t = window.Shopify && window.Shopify.theme;
    return !!r && r !== 'main' && !(t && t.role === 'main');
  }

  function envOverrideBase() {
    try {
      if (!slqEnvAllowed()) return '';
      var v = new URLSearchParams(window.location.search).get('slqEnv');
      return (v && ENV_BASES[v.toLowerCase()]) || '';
    } catch (e) { return ''; }
  }

  function readSettings() {
    var envBase = envOverrideBase();
    SLQ.settings = {
      nextBase: envBase || stsEnvBase('nextBase', root.getAttribute('data-next-base'), ''),
      ec2Base: stsEnvBase('apiBase', root.getAttribute('data-ec2-base'), ''),
      proxyBase: stsEnvBase('proxyBase', root.getAttribute('data-proxy-base'), '/apps/quiz'),
      useProxySubmit: root.getAttribute('data-use-proxy-submit') === 'true',
      turnstileSiteKey: root.getAttribute('data-turnstile-site-key') || '',
      // Blank = render results inline on this page (original behaviour).
      // Set = redirect to that URL after scoring, so the routine has its own page.
      resultsUrl: root.getAttribute('data-results-url') || '',
      // GA4 event names (theme editor > quiz section > Google Analytics events).
      // Blank = that event is off. See gaEvent() below.
      ga: {
        category: root.getAttribute('data-ga-category') || 'Custom Event',
        landed: root.getAttribute('data-ga-landed') || '',
        started: root.getAttribute('data-ga-started') || '',
        questionLoaded: root.getAttribute('data-ga-question-loaded') || '',
        questionAnswered: root.getAttribute('data-ga-question-answered') || '',
        completed: root.getAttribute('data-ga-completed') || ''
      }
    };
    if (envBase) {
      try {
        console.warn('[SLQ] API base overridden by ?slqEnv -> ' + envBase);
        var b = document.createElement('div');
        b.setAttribute('data-slq-env-banner', '');
        b.textContent = 'Preview: questions loaded from ' + envBase.replace('https://', '');
        b.style.cssText = 'position:fixed;left:0;right:0;bottom:0;z-index:2147483647;'
          + 'background:#2F3572;color:#fff;font:600 12px/1.5 system-ui,sans-serif;'
          + 'text-align:center;padding:6px 12px;letter-spacing:.02em';
        (document.body || document.documentElement).appendChild(b);
      } catch (e) { /* banner is advisory only */ }
    }
  }

  // Notes: docs/new-theme-v4/asset-notes/sts-liquid-quiz.md (resolveAffiliateCode).
  function resolveAffiliateCode() {
    try {
      var rawCode = new URLSearchParams(window.location.search).get('code');
      var parts = (rawCode || '').trim().split('-');
      var code = parts[0];
      var providerId = /^[A-Za-z0-9]{1,20}$/.test(parts[1] || '') ? parts[1] : '';
      if (AFF_RE.test(code)) {
        try {
          localStorage.setItem('fullCodeParam', rawCode.trim());
          localStorage.setItem('affiliateCode', code);
          sessionStorage.setItem('affiliateCode', code);
          if (providerId) {
            localStorage.setItem('providerId', providerId);
            sessionStorage.setItem('providerId', providerId);
          } else {
            localStorage.removeItem('providerId');
            sessionStorage.removeItem('providerId');
          }
        } catch (e) { /* noop */ }
        return code;
      }
    } catch (e) { /* noop */ }
    var s1 = (ssGet('affiliateCode') || '').trim(), s2 = (lsGet('affiliateCode') || '').trim();
    return AFF_RE.test(s1) ? s1 : AFF_RE.test(s2) ? s2 : null;
  }

  // ------------------------------------------- a friend's invite (v6.2 §4.4)
  // Behind the theme setting "Friend quiz: on" (data-friend-quiz="1"), or the
  // staff simulator's ?sts_friend=1 off the published theme, which shows the
  // notice from &sender= and never sends. ?invite=<id> is kept for the tab
  // (sessionStorage slqInvite) through to the submit. Before question 1 the
  // friend reads "[Sender] will see your skin type result" (theme settings, the
  // wording is pending Dr. Baumann and legal): Start sends friendInvite
  // {inviteId, shareConsent:true} with the result (wire contract 2026-09-28);
  // "Take the quiz without sharing" sends nothing. The sender's first name is
  // GET {proxy}/friend-quiz/invite/<id>; 404, 403 (off), completed or no
  // answer = no notice and nothing sent. Question 1 waits for that answer.
  var INVITE_KEY = 'slqInvite';
  var invite = null;          // {id, sender, share: null | true | false, demo}
  var invitePending = false;

  function inviteLoad() {
    if (!$('[data-slq-invite]')) return;
    var demo = slqEnvAllowed() && /[?&]sts_friend=1(&|#|$)/.test(window.location.search);
    if (root.getAttribute('data-friend-quiz') !== '1' && !demo) return;
    var q = new URLSearchParams(window.location.search), id = q.get('invite'), saved = null;
    try { saved = JSON.parse(ssGet(INVITE_KEY) || 'null'); } catch (e) { saved = null; }
    if (id) {
      if (!/^[A-Za-z0-9_-]{4,64}$/.test(id)) return;
      if (!saved || saved.id !== id) saved = { id: id, share: null };
      var who = String(q.get('sender') || '').replace(/[^\p{L}' .-]/gu, '').trim().slice(0, 40);
      if (who) saved.sender = who;
    }
    if (!saved || !saved.id) return;
    saved.demo = demo;
    ssSet(INVITE_KEY, JSON.stringify(saved));
    if (saved.share === true || saved.share === false) { invite = saved; return; }
    invitePending = true;
    netFetch(stripTrailingSlash(SLQ.settings.proxyBase) + '/friend-quiz/invite/' + encodeURIComponent(saved.id) + '?nonce=' + uuid(),
      { headers: { Accept: 'application/json' }, credentials: 'same-origin' }, 8000)
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; })
      .then(function (j) {
        invitePending = false;
        var open = !!(j && j.status === 'open');
        if (open && j.senderFirstName) saved.sender = String(j.senderFirstName).slice(0, 40);
        invite = open || demo ? saved : null;
        if (state.view === 'quiz' && state.questionsStatus === 'ready') startQuestionFlow();
      });
  }

  // true while the notice (or the wait for it) holds question 1 back
  function inviteGate() {
    var box = $('[data-slq-invite]');
    if (!box) return false;
    var main = $('[data-slq-quiz-main]'), load = $('[data-slq-quiz-loading]');
    if (invitePending) {
      if (main) main.setAttribute('hidden', '');
      if (load) load.removeAttribute('hidden');
      return true;
    }
    if (!invite || invite.share === true || invite.share === false) { box.setAttribute('hidden', ''); return false; }
    var name = invite.sender || 'The person who sent you this quiz';
    // The no-name fallback is a phrase, not a name: lower-case it mid-sentence.
    $all('[data-tpl]', box).forEach(function (el) {
      var tpl = el.getAttribute('data-tpl');
      var who = (!invite.sender && tpl.indexOf('[sender]') > 0) ? name.charAt(0).toLowerCase() + name.slice(1) : name;
      el.textContent = tpl.split('[sender]').join(who);
    });
    ['[data-slq-quiz-main]', '[data-slq-quiz-loading]', '[data-slq-quiz-error]'].forEach(function (s) { var n = $(s); if (n) n.setAttribute('hidden', ''); });
    box.removeAttribute('hidden');
    var h = $('[data-slq-invite-t]', box);
    if (h) { h.setAttribute('tabindex', '-1'); try { h.focus({ preventScroll: true }); } catch (e) { /* old engine */ } }
    return true;
  }

  function inviteAnswer(share) {
    if (!invite) return;
    invite.share = share;
    ssSet(INVITE_KEY, JSON.stringify(invite));
    var box = $('[data-slq-invite]');
    if (box) box.setAttribute('hidden', '');
    if (state.questionsStatus === 'ready') startQuestionFlow();
    else renderQuizStatus();
  }

  // ---------------------------------------------- affiliate question logic
  // Ports of assets/quiz.js's Octane-DOM customizations, applied to our own
  // renderer instead of MutationObservers.

  // quiz.js:65 — known-affiliate list; when the visitor arrived with a known
  // code the Practice Code input is hidden (they never edit it). Fail-open.
  var knownAffiliateCodes = null;
  try {
    /* The theme asset first. The hard-coded Files copy this used to read is a
       frozen 2023 upload: measured 2026-09-19 it holds 204 codes against the
       246 in assets/affiliateNames.json, and it is a strict subset — so for 42
       real practices the visitor arrived with a perfectly valid code, this list
       did not recognise it, and they got an editable Practice Code field
       instead of the "Practice code applied" confirmation. layout/theme.liquid
       publishes the theme asset's url as window.affiliateNamesJSONURL well
       before this deferred script runs; the Files url stays as the fallback so
       a page that does not set the global behaves exactly as it does today. */
    netFetch(window.affiliateNamesJSONURL ||
             'https://cdn.shopify.com/s/files/1/0740/5984/1838/files/affiliateNames.json?v=1692642219')
      .then(function (r) { return r.json(); })
      .then(function (d) {
        knownAffiliateCodes = (d || []).map(function (a) { return a.affiliateCode; });
      })
      .catch(function () { /* fail-open: input stays visible */ });
  } catch (e) { /* noop */ }

  function effectiveAffiliateCodeUpper() {
    var q1 = state.answers && state.answers.q1;
    var typed = (Array.isArray(q1) ? q1[0] : q1 || '').trim();
    var code = typed || state.affiliateCode || 'STSMD';
    return String(code).toUpperCase();
  }

  // quiz.js:123-150 — GETREFRESHED hides medical/body options on the concerns
  // question and the skin-issues question, and reveals aesthetic-only ones.
  // Questions are identified by option TEXT (same as legacy), never by id.
  var AFFILIATE_OPTION_RULES = {
    concerns: {
      identifiers: ['Acne Scars', 'Broken blood vessels', 'Cellulite'],
      hideForRefreshed: [
        'Broken blood vessels on face or body',
        'Leg veins',
        'Scalp redness or flaking',
        'Toenail or foot fungus (scaling on feet)'
      ],
      showForRefreshed: [
        'Hollow Under eyes',
        'Hip Dips',
        'Jaw Contouring',
        'Droopy/Bulbous Nose Tip',
        'Nasal Hump'
      ]
    },
    skinIssues: {
      identifiers: ['Dandruff or itching scalp', 'Dry Lips', 'Dry skin'],
      hideForRefreshed: [
        'Dandruff or itching scalp',
        'Weak nails',
        'Psoriasis',
        'Eczema'
      ],
      showForRefreshed: []
    }
  };

  function applyAffiliateOptionRules(body) {
    var isRefreshed = effectiveAffiliateCodeUpper() === 'GETREFRESHED';
    var buttons = body.querySelectorAll('.slq-opt');
    if (!buttons.length) return;
    var texts = [];
    buttons.forEach(function (b) { texts.push(b.textContent); });
    var config = null;
    Object.keys(AFFILIATE_OPTION_RULES).forEach(function (k) {
      var c = AFFILIATE_OPTION_RULES[k];
      var matches = c.identifiers.some(function (id) {
        return texts.some(function (t) { return t.indexOf(id) !== -1; });
      });
      if (matches && !config) config = c;
    });
    if (!config) return;
    buttons.forEach(function (b) {
      var t = b.textContent;
      var inHide = config.hideForRefreshed.some(function (x) { return t.indexOf(x) !== -1; });
      var inShow = config.showForRefreshed.some(function (x) { return t.indexOf(x) !== -1; });
      // Restoring visibility must put back the option's OWN display mode.
      // Setting '' clears the inline `display:flex` these buttons are built
      // with (renderQuestion), dropping them to `display:block` — the letter
      // badge is a flex child with min-width:28px and no width, so it then
      // stretches to fill the whole row as a flat ellipse and pushes the label
      // onto a second line. This hit every NON-GETREFRESHED visitor, since the
      // else-branch below reveals the hideForRefreshed options.
      if (isRefreshed) {
        if (inHide) b.style.display = 'none';
        else if (inShow) b.style.display = OPT_DISPLAY;
      } else {
        if (inHide) b.style.display = OPT_DISPLAY;
        else if (inShow) b.style.display = 'none';
      }
    });
    relabelVisibleOptions(body);
  }

  /* Letters are assigned at render time from the option's index in the FULL
     list, so any option hidden above leaves a gap in the sequence — "General
     concerns" showed … T, U, X with V and W missing. Re-letter what the
     visitor can actually see, after the show/hide pass. */
  function relabelVisibleOptions(body) {
    var visible = [];
    body.querySelectorAll('.slq-opt').forEach(function (b) {
      if (b.style.display !== 'none') visible.push(b);
    });
    visible.forEach(function (b, i) {
      var key = b.firstElementChild;
      if (key) key.textContent = optionLetter(i);
    });
  }

  function applyQuestionCustomizations(q, body) {
    // Practice code entry (q1): hide the input for known-affiliate arrivals
    // (quiz.js:62-78) — the code is already applied; the visitor just
    // continues. Shown as a confirmation note instead of a bare hidden field.
    if (q.id === 'q1') state.q1Locked = false;
    if (q.id === 'q1' && state.affiliateCode && knownAffiliateCodes &&
        knownAffiliateCodes.indexOf(state.affiliateCode) !== -1) {
      var inp = body.querySelector('input, textarea');
      if (inp) inp.style.display = 'none';
      // The box is hidden, so the visitor has no way to type or clear a code:
      // an empty draft here means "nothing to edit", NOT "cleared on purpose".
      // Without this lock Continue blanked state.affiliateCode and every
      // arrival with a recognised practice code submitted as STSMD.
      state.q1Locked = true;
      var note = document.createElement('div');
      note.style.cssText = 'padding:14px 16px;border:1.5px solid var(--slq-type-tint);background:var(--slq-type-tint);border-radius:14px;font-size:15px;color:#26284a';
      note.textContent = 'Practice code applied: ' + state.affiliateCode;
      body.appendChild(note);
    }

    // (The legacy IDEAL customizations — birthday copy, required zip, the
    // IDEALROUTINE discount — were removed 2026-08-06: Ideal Image is no
    // longer a partner. RADIANCE's email rule stays inherently satisfied by
    // the always-required email gate.)

    // Unknown-but-present codes: prefill the practice-code input like the
    // legacy flow prefilled Octane's (quiz.js setQuizIdFromParams:42-45).
    if (q.id === 'q1' && state.affiliateCode && (state.draft === '' || state.draft == null)) {
      var q1inp = body.querySelector('input, textarea');
      if (q1inp && q1inp.style.display !== 'none') {
        state.draft = state.affiliateCode;
        q1inp.value = state.affiliateCode;
      }
    }

    // NO identity prefill from customerQuizAnswers (q17 name / q18 birth month).
    // That key is written ONLY by the legacy flow and is not on the spec §3
    // allow-list, so this widget can read it but never refresh it — it decays
    // into a permanently stale identity. On a shared or kiosk browser it made
    // the quiz prefill a PREVIOUS visitor's name and birth date. The email box
    // still prefills, but from `customerEmail`, which is allow-listed and this
    // widget rewrites on every successful submit (see finishSuccess).
    // A SIGNED-IN customer's name / birth date / background do prefill, from
    // server-rendered data only: identityPrefill(), seeded in renderQuestion.

    applyAffiliateOptionRules(body);
  }

  // Same-origin referrer (the storefront sends it: strict-origin-when-cross-
  // origin) or an explicit ?start. The quiz page itself counts: its own
  // header CTA is an in-site quiz button too.
  function enteredInSite() {
    try {
      if (new URLSearchParams(window.location.search).has('start')) return true;
      return !!document.referrer && new URL(document.referrer).origin === window.location.origin;
    } catch (e) { return false; }
  }

  function init() {
    root = document.querySelector('[data-slq-root]');
    if (!root) return;
    readSettings();
    setTypeColors(null);
    state.affiliateCode = resolveAffiliateCode();
    inviteLoad();
    var invYes = $('[data-slq-invite-yes]'), invNo = $('[data-slq-invite-no]');
    if (invYes) invYes.addEventListener('click', function () { inviteAnswer(true); });
    if (invNo) invNo.addEventListener('click', function () { inviteAnswer(false); });

    wireLanding();
    gaEvent('landed');

    // The header/mobile menu's "Retake the Quiz" link is /pages/skin-type-quiz?retake
    // (legacy contract). Honour it: drop any checkpointed answers so the shopper
    // starts clean instead of resuming a half-finished run from this tab.
    var wantsRetake = false;
    try { wantsRetake = new URLSearchParams(window.location.search).has('retake'); } catch (e) { /* noop */ }
    if (wantsRetake) clearProgress();

    // Mid-quiz reload: saved progress resumes the quiz directly at the same
    // question (restoreProgress applies the answers once questions arrive).
    // v6.1 1.1: every quiz CTA inside the site opens question 1 (Q~ resumes
    // where they stopped); the landing is for outside traffic only (ads,
    // email, social). So a retake, ?start, or arriving from one of our own
    // pages skips it. The pre-paint guard in sections/sts-liquid-quiz.liquid
    // hid the landing for the same cases; it is lifted once this has decided.
    if (hasSavedProgress() || wantsRetake || enteredInSite()) startQuiz();
    root.removeAttribute('data-slq-direct');

    var cont = $('[data-slq-continue]');
    if (cont) cont.addEventListener('click', onContinue);
    var skipBtn = $('[data-slq-skip]');
    if (skipBtn) skipBtn.addEventListener('click', onSkip);
    var back = $('[data-slq-back]');
    if (back) back.addEventListener('click', goBack);
    var retry = $('[data-slq-quiz-retry]');
    if (retry) retry.addEventListener('click', function () {
      state.questionsStatus = 'idle';
      fetchQuestions();
    });

    var gateForm = $('[data-slq-gate-form]');
    if (gateForm) gateForm.addEventListener('submit', onGateSubmit);
    var gateEmailInput = $('[data-slq-gate-email]');
    if (gateEmailInput) {
      gateEmailInput.addEventListener('input', function () { scheduleGateEmailCheck(false); });
      gateEmailInput.addEventListener('blur', function () { scheduleGateEmailCheck(true); });
    }
    var gateHint = $('[data-slq-gate-hint]');
    if (gateHint) gateHint.addEventListener('click', function (ev) {
      var t = ev.target;
      var a = t && t.closest ? t.closest('[data-slq-gate-login]') : null;
      if (!a) return;
      ev.preventDefault();
      onGateLoginFirst();
      var form = $('[data-slq-gate-form]');
      if (!form) return;
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    });
    var gateSkip = $('[data-slq-gate-skip]');
    if (gateSkip) gateSkip.addEventListener('click', function () { proceedFromGate(true); });
    var gateBack = $('[data-slq-gate-back]');
    if (gateBack) gateBack.addEventListener('click', function () { closeGate(); });
    var gateBackdrop = $('[data-slq-gate-backdrop]');
    if (gateBackdrop) gateBackdrop.addEventListener('click', function () { closeGate(); });

    var submitRetry = $('[data-slq-submit-retry]');
    if (submitRetry) submitRetry.addEventListener('click', function () {
      // a fresh attempt-set: NEW submissionId, freshly built (byte-identical
      // within its own attempt-set); answers + email are preserved — plus a
      // freshly minted Turnstile token, without which the retry is guaranteed
      // to 403 on a single-use-token duplicate.
      retryWithFreshToken();
    });

    // prefetch questions so Start is instant; quiz view shows honest
    // skeleton/error states if this is still pending or failed
    fetchQuestions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
